import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  InputGroup,
  Accordion,
  Modal,
} from "react-bootstrap";
import { Autocomplete, Box, Chip, TextField, Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
// import ProductAndServiceSection from "../Enquiry/DynamicProductAndServiceForm";

import AgentDetailsSection from "../../../../Common/DynamicComponentsForEnquiryQuotationSalesOrder/AgentDetailsSection";
import AssignedMemberSection, {
  Member,
} from "../../../../Common/DynamicComponentsForEnquiryQuotationSalesOrder/AssignedMemberSection";
import BillingFromAddress from "../../../../Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";
import { AppDispatch } from "index";
import {
  fetchProductQuotationById,
  fetchServiceQuotationById,
} from "slices/quotation/thunk";
import { MecordColors } from "../../../../Constants/Color";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { Agent } from "Common/CommonEnquiryData/EnquiryType";
import { useQuotationCalculations } from "Common/Hooks/UseCalculationForQuotationAndSalesOrder";
import {
  AssignedMemberDetailRetrive,
  initialPBGDetail,
  initialSalesOrderData,
  SalesOrderDataRetrive,
} from "Common/CommonEnquiryData/SalesOrderRetriveProductType";
import { buildSalesOrderPayload } from "Common/CommonEnquiryData/SalesOrderProductPayloadType";
import { DEFAULT_ORGANISATION } from "pages/QuotationV2/Form/serviceInitialValues";
import FormBreadcrumb from "Common/Components/FormBreadcrumb/FormBreadcrumb";
import * as Yup from "yup";
import { useFormik, Formik, FormikProvider } from "formik";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import CurrencyExchangeSection from "Common/CurrencyExchangeSection/CurrencyExchangeSection";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import ProductAndServiceSection from "./DynamicProductAndServiceForm";
import FormActionButtons from "pages/QuotationV2/Form/Components/FormActionButtons";
import AddCustomerModal from "pages/QuotationV2/Form/Components/AddCustomerModal";
import AddProductModal from "pages/QuotationV2/Form/Components/AddProductModal";
import { fetchCompanyByDropDown } from "slices/Masters/company/thunk";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { ArrowBack } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { fetchCategories } from "slices/enquiry/thunk";
import AddressFormModal from "pages/Enquiry/Components/AddressFormModal";
import { fetchAllUsersDropdown } from "slices/UserManagement/user/thunk";
import TermsConditionsSection from "pages/QuotationV2/Form/Sections/TermsConditionsSection";
import RetentionDetails from "../../components/RetentaionDetails";
import NotesAndAttachments from "../../components/NotesAndAttachments";
import ScheduleFollowUp from "../../components/ScheduleFollowUp";
import { autocompleteDropdownStyles } from "Constants/Style";
import AddTags from "../../components/AddTagsForSalesOrder";
import { showConfirm } from "helpers/swalHelper";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const mapAssignedMembersFromQuotation = (assignedMembers: any[] = [], membersOptions: any[] = []) => {
  return assignedMembers
    .map((member: any) => {
      const userId = member.user_id || member.id;
      if (!userId) return null;
      const option = membersOptions.find((opt: any) => (opt.user_id || opt.id) === userId);
      const source = option || member;
      const firstName = source.first_name || member.first_name || "";
      const lastName = source.last_name || member.last_name || "";
      const fullName =
        source.full_name ||
        source.fullname ||
        source.name ||
        `${firstName} ${lastName}`.trim() ||
        source.username ||
        "";

      return {
        ...source,
        id: userId,
        user_id: userId,
        fullname: fullName,
        name: fullName,
        username: source.username || source.email || "",
      };
    })
    .filter(Boolean);
};
// Helper function to convert UTC date to local date string (YYYY-MM-DD format)
// Backend returns UTC dates, we need to convert to local for display
const formatDateToDisplay = (utcDateStr: string | null | undefined): string => {
  if (!utcDateStr) return "";
  try {
    // Parse UTC date string
    const utcDate = new Date(utcDateStr);
    if (isNaN(utcDate.getTime())) return "";

    // Get local date components
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getDate()).padStart(2, "0");

    // Return in YYYY-MM-DD format (for HTML date input)
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

// Helper function to get initials from first and last name
const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return (first + last).toUpperCase();
};

// Helper function to format full address with city, country, and pincode
const formatFullAddress = (addressObj: any): string => {
  if (!addressObj) return "";
  
  const parts = [];
  if (addressObj.address) parts.push(addressObj.address);
  if (addressObj.city) parts.push(addressObj.city);
  if (addressObj.country) parts.push(addressObj.country);
  if (addressObj.pin_code || addressObj.pincode) parts.push(addressObj.pin_code || addressObj.pincode);
  
  return parts.length > 0 ? parts.join(", ") : "";
};

// Helper function to transform terms and conditions - group by type to handle multiple options per type
const transformTermsAndConditions = (terms_and_conditions: any): any[] => {
  if (!terms_and_conditions) return [];

  // Handle object (non-array) case
  let termsArray: any[] = [];
  if (Array.isArray(terms_and_conditions)) {
    termsArray = terms_and_conditions;
  } else if (typeof terms_and_conditions === "object") {
    termsArray = Object.values(terms_and_conditions);
  } else {
    return [];
  }

  if (termsArray.length === 0) return [];

  // Check if already in form format (has description with newlines or id field)
  const isFormFormat = termsArray.some((term: any) =>
    (term.description && term.description.includes("\n")) || term.id
  );

  if (isFormFormat) {
    // Already in form format - group by type and merge options
    const groupedByType: Record<string, string[]> = {};
    termsArray.forEach((term: any) => {
      if (term.type) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        // Extract options from description (newline-separated) or from option/options field
        if (term.description && term.description.includes("\n")) {
          const options = term.description.split("\n").filter((opt: string) => opt.trim() !== "");
          options.forEach((opt: string) => {
            if (!groupedByType[term.type].includes(opt.trim())) {
              groupedByType[term.type].push(opt.trim());
            }
          });
        } else if (term.description) {
          const opt = term.description.trim();
          if (opt && !groupedByType[term.type].includes(opt)) {
            groupedByType[term.type].push(opt);
          }
        } else if (term.option) {
          const opt = term.option.trim();
          if (opt && !groupedByType[term.type].includes(opt)) {
            groupedByType[term.type].push(opt);
          }
        } else if (term.options) {
          const opt = typeof term.options === 'string' ? term.options.trim() : String(term.options).trim();
          if (opt && !groupedByType[term.type].includes(opt)) {
            groupedByType[term.type].push(opt);
          }
        }
      }
    });

    return Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "",
        description: options.join("\n"),
      })
    );
  } else {
    // Backend format: array of {type, options} - group by type
    const groupedByType: Record<string, string[]> = {};
    termsArray.forEach((term: any) => {
      if (term.type && term.options) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        const option = typeof term.options === 'string' ? term.options.trim() : String(term.options).trim();
        if (option && !groupedByType[term.type].includes(option)) {
          groupedByType[term.type].push(option);
        }
      }
    });

    return Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "",
        description: options.join("\n"),
      })
    );
  }
};

const normalizeAttachmentUrls = (attachments: any): string[] =>
  Array.isArray(attachments)
    ? attachments
        .map((attachment: any) =>
          typeof attachment === "string"
            ? attachment
            : attachment?.file_url || attachment?.url || "",
        )
        .filter(Boolean)
    : [];

// Re-define component props, referencing new types
export interface AddProductAndServicesSalesOrderFormProps {
  type?: "product"; // Product specific, defaults to product
  initialData: any; // The full API response
  companies: any[]; // Using 'any' as Company type not fully defined here
  // sources: string[];
  onSave: (payload: any, action: "draft" | "save") => Promise<void>;
  // categories: any[];
  // subcategories: any[];
  // makes: any[];
  // models: any[];
  // fetchSubcategories: (categoryId: string) => void;
  // fetchMakes?: (params: { categoryId: string; subcategoryId: string }) => void;
  // fetchModels?: (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  //   makeId: string;
  // }) => void;
  membersOptions: any[]; // <-- all assignable users
  // products?: {};
  enquiryReferences: string; // Should be array of objects for Autocomplete
  quotationRefenrence: any; // Should be array of objects for Autocomplete
  onQuotationSelect: (quotationId: string) => void;
  organizationsState?: any[]; // Optional - BillingFromAddress component will fetch its own
}

const AddProductAndServicesSalesOrderForm: React.FC<
  AddProductAndServicesSalesOrderFormProps
> = ({
  // type = "product",
  initialData,
  companies,
  // sources,
  onSave,
  // categories,
  // subcategories,
  // makes,
  // models,
  // fetchSubcategories,
  // fetchMakes,
  // fetchModels,
  membersOptions,
  // products = {},
  quotationRefenrence,
  onQuotationSelect,
  organizationsState,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false); // For data loading
    const [saving, setSaving] = useState(false); // For Save button
    const [savingDraft, setSavingDraft] = useState(false); // For Save and Draft button
    const [validationAttemptCount, setValidationAttemptCount] = useState(0);
    // const [formData, setFormData] = useState<SalesOrderDataRetrive>(
    //   initialSalesOrderData
    // );
    console.log("initialData in masterform => ", initialData)
    console.log("quotationRefenrence in line 116", quotationRefenrence);
    const [items, setItems] = useState<any[]>([]);
    const [shippingAddresses, setShippingAddresses] = useState<
      {
        id: string;
        address: string;
        state?: string;
        city?: string;
        pin_code?: string;
      }[]
    >([]);
    const productFormRef = useRef<any>(null);
    // Autocomplete option states (MUI requires objects)
    const [pbgStatusOptions] = useState(["Received", "Pending"]);
    const [statusOptions] = useState(["In Progress", "Completed", "Cancelled"]);
    const [selectedQuotation, setSelectedQuotation] = useState<any>(null);
    const [generalFile, setGeneralFile] = useState<File | null>(null);
    const isUserSelectingQuotation = useRef(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);

    const retentionTypeOptions = [
      { label: "Percentage", value: "percentage" },
      { label: "Amount", value: "amount" },
    ];

    interface CompositionOption {
      gst_id: string;
      gst_percentage: string;
    }
    const [compositionList, setCompositionList] = useState<CompositionOption[]>(
      []
    );

    const [currencyList, setCurrencyList] =
      useState<{ label: string; value: string }[]>();

    const [tableExchange, setTableExchange] = useState<string | number>("");
    const [technicianOptions, setTechnicianOptions] = useState<any[]>([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

    const validationSchema = Yup.object().shape({
      // Sales Order Details Validations
      sales_order_date: Yup.date()
        .required("Order date is required")
        .typeError("Invalid order date"),
      valid_until_date: Yup.date()
        .required("Valid Until Date is required")
        .typeError("Invalid Valid Until Date"),
      quotation_number: Yup.string().nullable(),
      quotation: Yup.string().nullable(), // can be null (no need for required)
      initiated_by: Yup.string().required("Initiated by is required"),
      status: Yup.string().nullable(), // dropdown — allow empty
      enquiry_number: Yup.string().nullable(), // autofill — no validation
      enquiry: Yup.string().nullable(), // can be null (no need for required)

      // Customer Details Validations
      company_id: Yup.string().nullable(),
      company_name: Yup.string().required("Company name is required"),
      contactPersonArray: Yup.array()
        .min(1, "At least select one contact person")
        .required("At least select one contact person"),
      industry_name: Yup.string().nullable(),
      company_email: Yup.string().email("Invalid email format").nullable(),
      billing_address: Yup.object().nullable(), // optional, autofill
      bill_from_address: Yup.mixed()
        .required("Bill From address is required")
        .test(
          "is-valid-bill-from-address",
          "Bill From address is required",
          (value: any) => {
            console.log("Validation test - value:", value, "type:", typeof value);
            if (!value) {
              console.log("Validation failed: value is falsy");
              return false;
            }
            // If it's a string, check if it's not empty
            if (typeof value === "string") {
              const isValid = value.trim() !== "";
              console.log("Validation (string):", isValid, "trimmed value:", value.trim());
              return isValid;
            }
            // If it's an object, check if it has org_address or address fields
            if (typeof value === "object" && !Array.isArray(value)) {
              // Check if it's an empty object
              if (Object.keys(value).length === 0) {
                console.log("Validation failed: empty object");
                return false;
              }
              // Check if it has org_address or address field
              const hasAddress = !!(value.org_address || value.address);
              console.log("Validation (object):", hasAddress, "org_address:", value.org_address, "address:", value.address);
              console.log("All object keys:", Object.keys(value));
              return hasAddress;
            }
            console.log("Validation failed: value is not string or object");
            return false;
          }
        ),
      // Contact persons (multiple)
      contact_details: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().nullable(),
            contact_person_name: Yup.string().required(
              "Contact person name is required"
            ),
            contact_person_email: Yup.string()
              .email("Invalid email format")
              .nullable(),
            contact_person_phone_no: Yup.string().nullable(),
            contact_person_department: Yup.string().nullable(),
            contact_person_designation: Yup.string().nullable(),
          })
        )
        .nullable(),
      // PO fields (top-level)
      customer_po_number: Yup.string().nullable(),
      customer_po_date: Yup.string().nullable(),
      customer_po_document_url: Yup.mixed().nullable(),

      // // Agent Details Validations
      // agent: Yup.object()
      //   .shape({
      //     agent_name: Yup.string().nullable(), // can be null
      //   })
      //   .nullable(),

      // Members Staff Validation
      // members: Yup.array()
      //   .of(
      //     Yup.object().shape({
      //       id: Yup.string().required(),
      //       fullname: Yup.string().required(),
      //       username: Yup.string().required(),
      //     })
      //   )
      //   .nullable(),

      // PBG Details Validations
      pbg_details: Yup.object({
        pbg_required: Yup.boolean().default(false),

        pbg_number: Yup.string().when("pbg_required", {
          is: true,
          then: (schema) => schema.required("PBG Number is required"),
          otherwise: (schema) => schema,
        }),

        pbg_date: Yup.string().when("pbg_required", {
          is: true,
          then: (schema) => schema.required("PBG Date is required"),
          otherwise: (schema) => schema,
        }),

        bank_name: Yup.string().when("pbg_required", {
          is: true,
          then: (schema) => schema.required("Bank Name is required"),
          otherwise: (schema) => schema,
        }),

        amount: Yup.number().when("pbg_required", {
          is: true,
          then: (schema) =>
            schema
              .required("Amount is required")
              .typeError("Amount must be a number")
              .min(0, "PBG Amount cannot be negative"),
          otherwise: (schema) => schema,
        }),

        validity: Yup.string().when("pbg_required", {
          is: true,
          then: (schema) => schema.required("Validity is required"),
          otherwise: (schema) => schema,
        }),

        pbg_status: Yup.string().when("pbg_required", {
          is: true,
          then: (schema) => schema.required("PBG Status is required"),
          otherwise: (schema) => schema,
        }),

        upload_document: Yup.mixed().nullable(),
      }),

      // Currency & GST/TAXATION
      currency_applicable: Yup.boolean().default(false),
      currency_code: Yup.string().default("INR"),
      currency_symbol: Yup.string().nullable(),

      exchange_rate: Yup.string().when(["currency_applicable", "currency_code"], {
        is: (applicable: boolean, code: string) => applicable && code !== "INR" && code !== "IND",
        then: (schema) => schema.required("Exchange rate is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      composition_gst: Yup.boolean(),

      select_composition_gst: Yup.string().when("composition_gst", {
        is: (val: boolean) => val === true,
        then: (schema) =>
          schema.required("Composition GST selection is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Payment Terms
      payment_terms: Yup.string().nullable(),
      payment_terms_amount: Yup.number().typeError("Must be a number").nullable().transform((v) => (v === "" || v === null || v === undefined ? null : v)),
      advance_payment: Yup.number().typeError("Must be a number").nullable(),
      pending_payment: Yup.number().typeError("Must be a number").nullable(),
      next_credit_schedule: Yup.string().nullable(),
      payment_reminder: Yup.number().typeError("Must be a number").nullable(),

      // Inspection & Installation Date
      inspection_applicable: Yup.boolean().default(false),
      inspection: Yup.string().when("inspection_applicable", {
        is: true,
        then: (schema) => schema.required("Inspection is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      inspection_date: Yup.string().when(["inspection_applicable", "inspection"], {
        is: (applicable: boolean, val: string) => applicable && val === "Applicable",
        then: (schema) => schema.required("Inspection Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      installation: Yup.string().when("inspection_applicable", {
        is: true,
        then: (schema) => schema.required("Installation is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      installation_date: Yup.string().when(["inspection_applicable", "installation"], {
        is: (applicable: boolean, val: string) => applicable && val === "Applicable",
        then: (schema) => schema.required("Installation Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // technicians: Yup.string().when("installation", {
      //   is: (val: string) => val === "Applicable",
      //   then: (schema) => schema.required("Installation Date is required"),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
      // technicians: Yup?.array().min(
      //   0, // Products don't require technicians
      //   "At least one technician must be assigned"
      // ),

      // Retention Details (optional)
      retention_details: Yup.object().shape({
        retention_applicable: Yup.boolean().default(false),
        retentionType: Yup.string().nullable(),
        percentage: Yup.number()
          .min(0, "Percentage cannot be negative")
          .max(100, "Percentage cannot be more than 100")
          .nullable(),
        retentionAmount: Yup.number().min(0, "Amount cannot be negative").nullable(),
        followUpDate: Yup.string().nullable(),
      }),

      next_follow_up_date: Yup.string().required("Next follow up date is required"),
      follow_up_remarks: Yup.string().nullable(),

      // EMD Details - mode_of_emd is optional, emd_amount required only when mode_of_emd is selected
      mode_of_emd: Yup.string().nullable(),
      emd_amount: Yup.number()
        .typeError("EMD Amount must be a number")
        .min(0, "EMD Amount cannot be negative")
        .when("mode_of_emd", {
          is: (val: string) => val && val.trim() !== "",
          then: (schema) => schema.required("EMD Amount is required when Mode of EMD is selected"),
          otherwise: (schema) => schema.nullable(),
        }),
    });

    // Helper function to get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // FORMIK
    const formik = useFormik<SalesOrderDataRetrive>({
      initialValues: {
        ...initialSalesOrderData,
        sales_order_date: initialSalesOrderData.sales_order_date || getCurrentDate(),
        currency_code: initialSalesOrderData.currency_code || "INR",
        exchange_rate: (initialSalesOrderData.currency_code === "INR" || initialSalesOrderData.currency_code === "IND" || !initialSalesOrderData.currency_code)
          ? "1"
          : (initialSalesOrderData.exchange_rate || ""),
        inspection: initialSalesOrderData.inspection || "Not Applicable",
        installation: initialSalesOrderData.installation || "Not Applicable",
      },
      validationSchema: validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: false,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          await handleSubmitAction(values, "save"); // Always "save" for submit
          
          // toast.success("Sales order saved successfully");
          // navigate(`/sales_order/product/sales_order_log`);
        } catch (err) {
          // Error is already handled in handleSubmitAction
          // Don't navigate on error
        } finally {
          setSubmitting(false);
        }
      },
    });

    useScrollToFormikError(formik.errors, formik.submitCount + validationAttemptCount);

    // Currency DropDown
    useEffect(() => {
      const fetchCurrency = async () => {
        try {
          const res = await Axios.get(`masters/currency/dropdown`);
          // console.log("Fetched dropdown:", res);
          const mappedCurrencyList = res.data?.data.map((item: any) => ({
            label: `${item.currency_name} (${item.currency_code})`,
            value: item.currency_code,
            currency_id: item.currency_id,
            currency_name: item.currency_name,
            currency_symbol: item.currency_symbol,
          }));

          setCurrencyList(mappedCurrencyList);

          // Sync currency code and symbol
          const currentCurrencyCode = formik.values.currency_code;
          const currentCurrencySymbol = formik.values.currency_symbol;

          // If we have a currency_code, sync the symbol
          if (currentCurrencyCode && !currentCurrencySymbol) {
            const selectedCurrency = mappedCurrencyList.find(
              (c: any) => c.value === currentCurrencyCode
            );
            if (selectedCurrency) {
              formik.setFieldValue("currency_symbol", selectedCurrency.currency_symbol);
            } else if (currentCurrencyCode === "INR" || currentCurrencyCode === "IND") {
              formik.setFieldValue("currency_symbol", "₹");
            }
          }

          // If we have a foreign currency_symbol but currency_code is INR, find the correct code
          if (currentCurrencySymbol && currentCurrencySymbol !== "₹" && (currentCurrencyCode === "INR" || currentCurrencyCode === "IND" || !currentCurrencyCode)) {
            const matchingCurrency = mappedCurrencyList.find(
              (c: any) => c.currency_symbol === currentCurrencySymbol
            );
            if (matchingCurrency && matchingCurrency.value) {
              // console.log("Updating currency code to:", matchingCurrency.value, "for symbol:", currentCurrencySymbol);
              formik.setFieldValue("currency_code", matchingCurrency.value);
            }
          }
        } catch (err: any) {
          // toast.error("Failed to fetch currency list:", err.message || err.error || "Failed to fetch currency list");
          // console.error("Failed to fetch currency list:", err);
        }
      };

      fetchCurrency();
    }, [dispatch]);

    // Fetch GST Dropdown
    useEffect(() => {
      const fetchGST = async () => {
        try {
          const res = await Axios.get(`masters/gst/dropdown`);

          // console.log("Mapped", mappedCurrencyList);
          setCompositionList(res?.data?.data || res.data);
        } catch (err: any) {
          // toast.error("Failed to fetch currency list:", err.message || err.error || "Failed to fetch currency list");
          // console.error("Failed to fetch currency list:", err);
        }
      };

      fetchGST();
    }, [dispatch]);

    // Fetch Technician Dropdown
    useEffect(() => {
      const fetchTechnician = async () => {
        try {
          const res = await dispatch(fetchAllUsersDropdown()).unwrap();
          // console.log("Fetched Technician dropdown:", res);
          setTechnicianOptions(res?.data?.data || res.data);
        } catch (err: any) {
          // toast.error("Failed to fetch Technician list:", err.message || err.error || "Failed to fetch Technician list");
          // console.error("Failed to fetch Technician list:", err);
        }
      };

      fetchTechnician();
    }, [dispatch]);

    // Reactively update shipping addresses and contact details when companies data changes (e.g. after adding a new address or contact)
    useEffect(() => {
      if (formik.values.company && companies.length > 0) {
        const currentCompanyId = formik.values.company.company_id || formik.values.company.id;
        const updatedCompany = companies.find((c) => c.id === currentCompanyId);

        if (updatedCompany) {
          // Update shipping addresses
          if (updatedCompany.shipping_addresses) {
            const formattedAddresses = updatedCompany.shipping_addresses.map(
              (addr: any) => ({
                id: addr.id,
                address:
                  addr.address || `${addr.city}, ${addr.state}, ${addr.pin_code}`,
                state: addr.state,
                city: addr.city,
                pin_code: addr.pin_code,
                phone_number: addr.phone_number,
                country: addr.country || "",
              })
            );

            if (JSON.stringify(formattedAddresses) !== JSON.stringify(shippingAddresses)) {
              setShippingAddresses(formattedAddresses);
            }
          }

          // Update contact details - transform all contacts from company to form format
          const masterContacts = (updatedCompany.contact_details || updatedCompany.contacts || []).map((contact: any) => ({
            id: contact.contact_id || contact.id || "",
            contact_id: contact.contact_id || contact.id || "",
            contact_person_name: contact.name || contact.contact_person_name || "",
            contact_person_email: contact.email || contact.contact_person_email || "",
            contact_person_phone_no: contact.phone_number || contact.contact_person_phone_no || "",
            contact_person_designation: contact.designation || contact.contact_person_designation || "",
            contact_person_department: contact.contact_person_department || contact.department || "",
          }));

          // Merge with existing contacts in formik (which might have come from quotation)
          // Master contacts are prioritized/shown first
          const existingContacts = formik.values.company?.contact_details || [];
          const mergedContacts = [...masterContacts];

          existingContacts.forEach((exc: any) => {
            const alreadyPresent = mergedContacts.some(mc =>
              (mc.id && exc.id && mc.id === exc.id) ||
              (mc.contact_id && exc.contact_id && mc.contact_id === exc.contact_id) ||
              (mc.contact_person_email && exc.contact_person_email && mc.contact_person_email.toLowerCase() === exc.contact_person_email.toLowerCase())
            );
            if (!alreadyPresent) {
              mergedContacts.push(exc);
            }
          });

          // Update company object with merged contacts so dropdown shows all available contacts
          const updatedCompanyForForm = {
            ...formik.values.company,
            contact_details: mergedContacts,
          };

          // Only update if changed to avoid unnecessary re-renders
          if (JSON.stringify(mergedContacts) !== JSON.stringify(formik.values.company?.contact_details)) {
            formik.setFieldValue("company", updatedCompanyForForm, false); // Update reference to avoid stale data
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies, formik.values.company?.id, formik.values.company?.company_id]);


    const totals = useQuotationCalculations(
      items,
      "sales_order",
      formik?.values?.bill_from_address,
      shippingAddresses,
      formik
    );

    // Sync calculated totals to formik values so they're included in the payload
    // Use ref to track previous totals to prevent unnecessary updates
    const prevTotalsRef = useRef<any>(null);
    useEffect(() => {
      if (totals && formik) {
        // Only update if totals have actually changed
        const totalsChanged =
          !prevTotalsRef.current ||
          prevTotalsRef.current.subTotal !== totals.subTotal ||
          prevTotalsRef.current.discount !== totals.discount ||
          prevTotalsRef.current.gst !== totals.gst ||
          prevTotalsRef.current.netTotal !== totals.netTotal ||
          prevTotalsRef.current.grandTotal !== totals.grandTotal;

        if (totalsChanged) {
          // Use setFieldValue with validate: false to prevent validation loops
          formik.setFieldValue("subtotal_before_discount", totals.subTotal.toString(), false);
          formik.setFieldValue("total_item_discounts", totals.discount.toString(), false);
          formik.setFieldValue("total_gst", totals.gst.toString(), false);
          formik.setFieldValue("net_taxable_value", totals.netTotal.toString(), false);
          formik.setFieldValue("total_amount", totals.grandTotal.toString(), false);
          formik.setFieldValue("total_gst_value", totals.gst.toString(), false);
          prevTotalsRef.current = { ...totals };
        }
      }
    }, [totals?.subTotal, totals?.discount, totals?.gst, totals?.netTotal, totals?.grandTotal]);

    // Sync selectedMemberIds with formik.values.members
    useEffect(() => {
      const memberIds = (formik.values.members || []).map((m: any) => m.id || m.user_id).filter(Boolean);
      setSelectedMemberIds(memberIds);
    }, [formik.values.members]);

    // Handler for adding a member
    const handleMemberAdd = (user: any) => {
      const userId = user.user_id || user.id;
      if (!selectedMemberIds.includes(userId)) {
        setSelectedMemberIds([...selectedMemberIds, userId]);
        const newMember = {
          id: userId,
          name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || user.email || "",
          initials: getInitials(user.first_name || "", user.last_name || ""),
        };
        formik.setFieldValue("members", [...(formik.values.members || []), newMember]);
      }
    };

    // Handler for removing a member
    const handleMemberRemove = (memberId: string) => {
      setSelectedMemberIds(selectedMemberIds.filter((id) => id !== memberId));
      formik.setFieldValue(
        "members",
        (formik.values.members || []).filter((m: any) => (m.id || m.user_id) !== memberId)
      );
    };

    // useEffect(() => {
    //   console.log("Formik Values:", formik.values);
    //   console.log("Formik Errors:", formik.errors);
    //   console.log("Formik Touched:", formik.touched);
    // }, [formik.values, formik.errors, formik.touched]);

    // Handler for adding items from ProductAndServiceSection
    const handleProductAdd = (item: any) => {
      // console.log("item in handleProductAdd: ", item);

      // Check for duplicate items
      setItems((prevItems) => {
        // Check if item with same item ID already exists
        const isDuplicateById = prevItems.some(
          (existingItem) => existingItem.item && existingItem.item === item.item
        );

        // Check if item with same item_name already exists (for products/services)
        const itemName = item.productName;
        const isDuplicateByName = prevItems.some((existingItem) => {
          const existingItemName =
            existingItem.productName;
          return (
            existingItemName &&
            existingItemName.toLowerCase() === itemName?.toLowerCase()
          );
        });

        if (isDuplicateById || isDuplicateByName) {
          toast.error(
            "This item is already added. Please select a different item."
          );
          return prevItems; // Don't add the duplicate item
        }

        // If no duplicate, add the item
        return [...prevItems, item];
      });
    };

    // mapped item details
    const mapQuotationItemToFormItem = (item: any) => {
      // Convert numeric strings to actual numbers for easier calculation in the UI
      // console.log("item in mapQuotationItemToFormItem: ", item);
      const toNum = (value: any) => (value ? Number(value) : 0);
      const roundToTwoDecimals = (num: number): number => {
        return Math.round(num * 100) / 100;
      };

      // Calculate discount fields before creating the return object
      const discountType = (item.discount_type === "%" || item.discount_type === "percentage") ? "percentage" : "currency";
      const discountValue = toNum(item.discount_value || 0);
      const quantity = toNum(item.product_quantity);
      const unitPrice = toNum(item.unit_price || item.rate_in_currency);
      const grossValue = toNum(item.gross_value || (unitPrice * quantity));

      // Calculate discount_amount based on discount_type
      let discountAmount = 0;
      if (discountType === "percentage" && discountValue > 0) {
        discountAmount = grossValue > 0 ? roundToTwoDecimals((grossValue * discountValue) / 100) : 0;
      } else if (discountType === "currency" && discountValue > 0) {
        discountAmount = roundToTwoDecimals(discountValue);
      }

      return {
        // Core Identifiers and Names
        id: item.quotation_product_item_id || item.id,
        item_name: item.product_name,
        item_type: "products", // Always products for quotation items
        category_name: item.category_name,
        sub_category_name: item.sub_category || item.sub_category_name,
        make_name: item.make_name,
        model_name: item.model_name,

        // Product details
        product_sku: item.product_sku,
        hsn_code: item.hsn_code,
        unit_name: item.unit_name,

        // Financials (Converted to Number for better UI interaction)
        quantity: quantity,
        unit_price: unitPrice,

        // Discount handling
        discount_type: discountType,
        discount_percent: (discountType === "percentage")
          ? (toNum(item.discount_percent || item.discount || discountValue))
          : null,
        discount_value: discountValue,
        discount_amount: discountAmount,

        // Currency Details
        currency_code: initialData.currency_code || "INR",
        exchange_rate: initialData.currency_code !== "INR" && initialData.currency_code !== "IND" ? (initialData.exchange_rate || initialData.currency_rate_in_inr || "1") : "1",

        // GST/Tax Details
        gst_percent: toNum(item.gst_percentage || 0),
        gst_amount: toNum(item.gst_value || 0),
        original_gst_percent: toNum(item.gst_percentage || 0),
        gst_value: toNum(item.gst_value || 0), // For compatibility with calculation hook
        original_gst_value: toNum(item.gst_value || 0), // Preserve original GST from quotation

        // Calculate CGST, SGST, IGST (assuming equal split for CGST/SGST, IGST = 100% if interstate)
        cgst_percent: toNum(item.cgst_percentage || 0) / 2, // Assuming equal split
        sgst_percent: toNum(item.sgst_percentage || 0) / 2, // Assuming equal split
        igst_percent: toNum(item.igst_percentage || 0), // Will be calculated based on billing/shipping addresses

        // Values
        gross_value: grossValue,
        net_value: toNum(item.product_net_total || item.gross_value),
        total_amount: toNum(item.product_total),
        // total_amount: toNum(item.product_total).toString(), // Set the display total amount

        // Serviceability & Dates (from quotation level, not item level)
        is_serviceable: item.serviceable || false, // Default to false, can be overridden
        installation: item.installation || false,
        installation_date: item.installation_date || null,
        inspection: item.inspection || false,
        inspection_date: item.inspection_date || null,

        // Foreign Keys (UUIDs) - Keep as strings
        item: item.product_id,
        category: null, // Not provided in quotation data
        sub_category: null, // Not provided in quotation data
        make: null, // Not provided in quotation data
        model: null, // Not provided in quotation data

        // Shipping Address - use item-level shipping if available, otherwise quotation-level
        shipping_address_id: item.shipping_address?.shipping_address_id || item.shipping_address?.id || null,

        // Additional fields
        remark: item.remark,
        serial_number: item.serial_number,

        // Quotation reference
        product_quotation: item.quotation_product_id,
      };
    };


    // ---- Fetch and Load Logic (Main useEffect) ----
    useEffect(() => {
      // Only load data if initialData has actual data (not empty object)
      if (!initialData || (!initialData.id && !initialData.quotation_product_id && !initialData.quotation_service_id)) {
        // Set default date if no initial data
        if (!formik.values.sales_order_date) {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const day = String(today.getDate()).padStart(2, "0");
          formik.setFieldValue("sales_order_date", `${year}-${month}-${day}`);
        }
        return;
      }

      // console.log("Loading quotation data:", initialData);
      // console.log("Has quotation_product_id:", !!initialData.quotation_product_id);
      setLoading(true);

      try {
        // Create Sales Order from Quotation - data will be handled by formik initial values
        if (initialData) {
          // console.log("Setting form values from quotation data");

          // Extract customer details from quotation data
          const customerDetails = initialData.customer_details || {};
          const companyIdFromQuotation = customerDetails.company_id || customerDetails.customer_id;

          // Find the full company object from the companies array
          const companyObj = companyIdFromQuotation
            ? companies.find((c) => c.id === companyIdFromQuotation || c.company_id === companyIdFromQuotation) || null
            : null;

          // Extract shipping addresses from the company object
          let formattedAddresses: any[] = [];
          if (companyObj?.shipping_addresses) {
            formattedAddresses = companyObj.shipping_addresses.map(
              (addr: any) => ({
                id: addr.id || addr.shipping_address_id,
                address: addr.address || `${addr.city}, ${addr.state}, ${addr.pin_code}`,
                state: addr.state,
                city: addr.city,
                pin_code: addr.pincode || addr.pin_code,
                phone_number: addr.phone_number,
              })
            );
            setShippingAddresses(formattedAddresses);
          }

          // Transform contacts from quotation's customer_details to form format (for auto-selection)
          const quotationContacts = (customerDetails.contact_details || customerDetails.contacts || []).map((contact: any) => ({
            id: contact.contact_id || contact.id || "",
            contact_person_name: contact.name || contact.contact_person_name || "",
            contact_person_email: contact.email || contact.contact_person_email || "",
            contact_person_phone_no: contact.phone_number || contact.contact_person_phone_no || "",
            contact_person_designation: contact.designation || contact.contact_person_designation || "",
            contact_person_department: contact.contact_person_department || "",
          }));

          // Get ALL contacts from the company object (not just from quotation)
          // This ensures all available contacts are shown in the dropdown
          const masterContacts = companyObj
            ? (companyObj.contact_details || companyObj.contacts || []).map((contact: any) => ({
              id: contact.contact_id || contact.id || "",
              contact_id: contact.contact_id || contact.id || "",
              contact_person_name: contact.name || contact.contact_person_name || "",
              contact_person_email: contact.email || contact.contact_person_email || "",
              contact_person_phone_no: contact.phone_number || contact.contact_person_phone_no || "",
              contact_person_designation: contact.designation || contact.contact_person_designation || "",
              contact_person_department: contact.contact_person_department || "",
            }))
            : [];

          // Merge quotation contacts with master contacts
          // Prioritize master contacts in the list
          const mergedContacts = [...masterContacts];
          quotationContacts.forEach((qc: any) => {
            const alreadyPresent = mergedContacts.some((mc: any) =>
              (mc.id && qc.id && mc.id === qc.id) ||
              (mc.contact_id && qc.contact_id && mc.contact_id === qc.contact_id) ||
              (mc.contact_person_email && qc.contact_person_email && mc.contact_person_email.toLowerCase() === qc.contact_person_email.toLowerCase())
            );
            if (!alreadyPresent) {
              mergedContacts.push(qc);
            }
          });

          // Create company object with proper structure for autocomplete
          // Use merged contacts so user can select from all available contacts
          const companyForForm: any = companyObj ? {
            id: companyObj.id || companyObj.company_id,
            company_id: companyObj.company_id || companyObj.id,
            company_name: companyObj.company_name || customerDetails.company_name || customerDetails.customer_name || "",
            company_email: companyObj.company_email || customerDetails.company_email || customerDetails.email || "",
            vendor_code: companyObj.vendor_code || customerDetails.vendor_code || "",
            branch_name: companyObj.branch_name || customerDetails.branch_name || "",
            industry: companyObj.industry || customerDetails.industry || "",
            industry_name: companyObj.industry || customerDetails.industry || "",
            contact_details: mergedContacts, // Use merged contacts
            gstin: companyObj.gstin || customerDetails.gstin || "",
            gst_type: companyObj.gst_type || customerDetails.gst_type || customerDetails.type || "",
            phone_number: companyObj.phone_number || customerDetails.phone_number || "",
            sr_no: companyObj.sr_no || 1,
            billing_address: companyObj.billing_address || customerDetails.billing_address,
          } : (companyIdFromQuotation ? {
            // Fallback if company not found in companies list yet
            id: companyIdFromQuotation,
            company_id: companyIdFromQuotation,
            company_name: customerDetails.company_name || customerDetails.customer_name || "",
            company_email: customerDetails.company_email || customerDetails.email || "",
            industry: customerDetails.industry || "",
            industry_name: customerDetails.industry || "",
            contact_details: quotationContacts,
            gstin: customerDetails.gstin || "",
            gst_type: customerDetails.gst_type || customerDetails.type || "",
            phone_number: customerDetails.phone_number || "",
            sr_no: 1,
            billing_address: customerDetails.billing_address,
          } : null);

          // Map quotation data to form fields
          const mappedData = {
            ...formik.values,
            // Basic quotation info
            quotation: initialData.quotation_product_id || initialData.quotation_service_id || initialData.id,
            quotation_number: initialData.quotation_number || "",
            quotation_date: formatDateToDisplay(initialData.quotation_date),
            // valid_until_date: getCurrentDate(),
            initiated_by: "",
            reference_number: initialData.reference_number || "",

            // Enquiry info
            enquiry: initialData.enquiry_product_id || initialData.enquiry_service_id || initialData.enquiry || "",
            enquiry_number: initialData.enquiry_number || "",

            // Customer details - auto-fill from quotation
            company: companyForForm,
            company_name: companyForForm?.company_name || customerDetails.company_name || customerDetails.customer_name || "",
            company_id: companyForForm?.company_id || companyIdFromQuotation || "",
            company_email: companyForForm?.company_email || customerDetails.company_email || customerDetails.email || "",
            industry: companyForForm?.industry || customerDetails.industry || "",
            industry_name: companyForForm?.industry_name || customerDetails.industry || "",

            // GST details - auto-fill from quotation
            gstin: companyForForm?.gstin || customerDetails.gstin || "",
            gst_type: companyForForm?.gst_type || customerDetails.gst_type || customerDetails.type || "",

            // Contact person array - auto-select first contact from quotation (if available)
            // But all company contacts will be available in the dropdown
            contactPersonArray: quotationContacts.length > 0 ? [quotationContacts[0]] : [],
            contactPerson: quotationContacts.length > 0 ? quotationContacts[0] : {},

            // Billing address - auto-fill from quotation
            billing_address: (() => {
              const billingAddr = customerDetails.billing_address || companyObj?.billing_address;
              if (!billingAddr) {
                return {
                  address: "",
                  city: "",
                  state: "",
                  country: "",
                  pin_code: "",
                  phone_number: "",
                };
              }
              return {
                address: billingAddr.address || "",
                city: billingAddr.city || "",
                state: billingAddr.state || "",
                country: billingAddr.country || "",
                pin_code: billingAddr.pincode || billingAddr.pin_code || "",
                phone_number: billingAddr.phone_number || "",
              };
            })(),

            // Shipping addresses - auto-fill from quotation
            shipping_addresses: formattedAddresses,

            // Financial details
            currency_code: initialData.currency_code || "INR",
            currency_symbol: initialData.currency_symbol || "₹",
            exchange_rate: (initialData.currency_code === "INR" || initialData.currency_code === "IND" || !initialData.currency_code)
              ? "1"
              : (initialData.currency_rate_in_inr || "1"),

            // EMD details
            mode_of_emd: initialData.mode_of_emd || "",
            emd_amount: initialData.emd_amount || "",

            // Inspection & Installation
            inspection_applicable: !!(initialData.inspection || initialData.installation),
            inspection: initialData.inspection || "Not Applicable",
            inspection_date: formatDateToDisplay(initialData.inspection_date),
            installation: initialData.installation || "Not Applicable",
            installation_date: formatDateToDisplay(initialData.installation_date),

            // PO details (if any)
            customer_po_number: initialData.customer_po_number || "",
            customer_po_date: formatDateToDisplay(initialData.customer_po_date),

            // Notes and attachments
            notes: "",
            attachments: normalizeAttachmentUrls(initialData.attachments),
            tags: initialData?.tags || [],

            // Terms and conditions
            terms_and_conditions: [],

            // Agent name (if any)
            agent_name: initialData.agent_name || "",
            members: mapAssignedMembersFromQuotation(initialData.assigned_members || [], membersOptions),
            bill_from_address: initialData.bill_from || DEFAULT_ORGANISATION,

            // Status (default to empty or appropriate status)
            status: "",

            // Sales Order Date - default to current date if not provided
            sales_order_date: (() => {
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, "0");
              const day = String(today.getDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            })(),

            // Composition GST - auto-fill from quotation if available
            composition_gst: initialData.composition_gst || false,
            select_composition_gst: initialData.select_composition_gst || "",
          };

          // console.log("Mapped data - contactPersonArray:", mappedData.contactPersonArray);
          // console.log("Mapped data - company.contact_details:", mappedData.company?.contact_details);
          formik.setValues(mappedData as any);
          // console.log("Form values after setting - contactPersonArray:", formik.values.contactPersonArray);
          // console.log("Form values after setting - company.contact_details:", formik.values.company?.contact_details);

          // Set items if they exist (handle both product and service quotations)
          const quotationItems = initialData.quotation_product_items || initialData.quotation_service_items;
          console.log("quotation items => in masterform", quotationItems)
          if (quotationItems && Array.isArray(quotationItems)) {
            const mappedItems = quotationItems.map((item: any) => mapQuotationItemToFormItem(item));
            // console.log("mappedItems in line 809", mappedItems)
            // console.log("Setting items from quotation:", mappedItems.map(item :any => ({
            //   product_name: item.product_name,
            //   gst_percent: item.gst_percent,
            //   gst_value: item.gst_value,
            //   gst_amount: item.gst_amount
            // })));
            // console.log("quotationItems => in masterform",quotationItems)
            // console.log("mappedItems => in masterform", mappedItems)
            setItems(mappedItems && mappedItems.length > 0 ? mappedItems : []);
          }

          // Shipping addresses are already set above if company was found
        }
      } catch (error) {
        // console.error("Failed to load data:", error);
        toast.error("Failed to load data for the form.");
      } finally {
        setLoading(false);
      }
    }, [initialData]);


    //   e.preventDefault();

    //   if (formData.members.length === 0) {
    //     toast("Please select at least one member");
    //     return;
    //   }
    //   console.log("formData and items on submit: ", formData, items);
    //   console.log("type", type);
    //   const payload = buildSalesOrderPayload(
    //     formData,
    //     items,
    //     type,
    //     shippingAddresses,
    //     organizationsState
    //   );

    //   console.log("✅ Final Sales Order Payload:", payload);

    //   try {
    //     setLoading(true);

    //     // await onSave(payload, action);
    //     // Navigation is handled by parent form component
    //   } catch (err: any) {
    //     toast.error(err?.message || "Operation failed.");
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    // console.log("formik values in line 1105", formik.values)
    const handleSubmitAction = async (
      values: SalesOrderDataRetrive,
      action: "draft" | "save"
    ) => {
      // Validate items before submission
      if (!items || items.length === 0) {
        // toast.error("At least one product is required");
        return;
      }
      
      const payload = buildSalesOrderPayload(
        values,
        items,
        // type,
        action,
        shippingAddresses,
        organizationsState,
        false // isEdit = false for create form
      );
      // console.log("payload in line 1146", payload)
      try {
        // Set appropriate loading state based on action
        if(items && items.length >= 1){
        if (action === "draft") {
          setSavingDraft(true);
        } else {
          setSaving(true);
        }
        // console.log("payload in line 1043", payload);
        await onSave(payload, action);
      }
      } catch (err: any) {
        // Extract error message from various possible error structures
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data ||
          err?.message ||
          (typeof err === 'string' ? err : "Operation failed.");

        toast.error(errorMessage);
        throw err; // Re-throw error so caller knows it failed
      } finally {
        // Reset appropriate loading state based on action
        if (action === "draft") {
          setSavingDraft(false);
        } else {
          setSaving(false);
        }
      }
    };
    // console.log("totals = > ", totals)
    // console.log("shipping addresses in line 1184", shippingAddresses)

    return (
      <PageContainer>


        {/* Top Action Buttons */}
        <FormBreadcrumb
          formName={
            "Sales Order"
          }
          isEdit={false}
          navigatePath={
            "/sales_order/product/sales_order_log"

          }
        />
        <style>
          {`
                  .accordion-terms-conditions .accordion-button,
                  .accordion-notes .accordion-button,
                  .accordion-attach-files .accordion-button {
                    background-color: ${MecordColors?.LightThemeBGColor} !important;
                    color: ${MecordColors?.ActiveText} !important;
                  }
                  .accordion-terms-conditions .accordion-button:not(.collapsed),
                  .accordion-notes .accordion-button:not(.collapsed),
                  .accordion-attach-files .accordion-button:not(.collapsed) {
                    background-color: ${MecordColors?.LightThemeBGColor} !important;
                    color: ${MecordColors?.ActiveText} !important;
                  }
                `}
        </style>
        <hr />

        <Formik
          initialValues={initialSalesOrderData}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await handleSubmitAction(values, "save");
            setSubmitting(false);
          }}
          enableReinitialize={false}
        >
          {(formikProps: any) => {
            const { values, handleSubmit, setFieldValue, validateForm, setTouched } = formikProps;
            return (
              <>
                <Form onSubmit={formik.handleSubmit}>
                  <style>
                    {`
                        .accordion-terms-conditions .accordion-button,
                        .accordion-notes .accordion-button,
                        .accordion-attach-files .accordion-button {
                          background-color: ${MecordColors?.LightThemeBGColor} !important;
                          color: ${MecordColors?.ActiveText} !important;
                        }
                        .accordion-terms-conditions .accordion-button:not(.collapsed),
                        .accordion-notes .accordion-button:not(.collapsed),
                        .accordion-attach-files .accordion-button:not(.collapsed) {
                          background-color: ${MecordColors?.LightThemeBGColor} !important;
                          color: ${MecordColors?.ActiveText} !important;
                        }
                      `}
                  </style>

                  {/* Sales Order Details & Customer Details Section */}
                  <Row className="mb-3">
                    {/* Sales Order Details*/}
                    <Col xs={12} md={6}>
                      <div className="rounded mb-3">
                        <div className="d-flex justify-content-start mb-3">
                          <h5 className="fw-semibold mb-2">Sales Order Details</h5>
                        </div>
                        <Row
                          className="g-3 pb-3 px-1 rounded"
                          style={{ backgroundColor: MecordColors?.LightGrayColor }}
                        >
                          {/* Sales Order Date */}
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                Sales Order Date{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <Form.Control
                                type="date"
                                name="sales_order_date"
                                value={formik.values.sales_order_date}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.sales_order_date &&
                                  !!formik.errors.sales_order_date
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.sales_order_date}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {/* Valid Until Date */}
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                Valid Until Date <span className="text-danger">*</span>
                              </Form.Label>

                              <Form.Control
                                type="date"
                                name="valid_until_date"
                                autoComplete="off"
                                value={formik.values.valid_until_date}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.valid_until_date && !!formik.errors.valid_until_date
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.valid_until_date}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {/* QUOTATION AUTOCOMPLETE */}
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                Quotation Number
                                {/* <span className="text-danger">*</span> */}
                              </Form.Label>

                              <Autocomplete
                                options={
                                  Array.isArray(quotationRefenrence)
                                    ? quotationRefenrence
                                    : []
                                }
                                value={
                                  formik.values.quotation && formik.values.quotation_number ? {
                                    quotation_product_id: formik.values.quotation,
                                    quotation_number: formik.values.quotation_number,
                                    id: formik.values.quotation
                                  } : null
                                }
                                getOptionLabel={(option) =>
                                  option.quotation_number}
                                isOptionEqualToValue={(option, value) =>
                                  option?.quotation_product_id === value?.quotation_product_id ||
                                  option?.id === value?.id ||
                                  option?.quotation_product_id === value?.id
                                }
                                onChange={(_, newValue) => {
                                  // console.log("Quotation selected:", newValue);
                                  if (newValue) {
                                    formik.setFieldValue("quotation", newValue.quotation_product_id || newValue.id);
                                    formik.setFieldValue(
                                      "quotation_number",
                                      newValue.quotation_number
                                    );
                                    onQuotationSelect(newValue.quotation_product_id || newValue.id);
                                  } else {
                                    // Clear all quotation-related fields
                                    formik.setFieldValue("quotation", "");
                                    formik.setFieldValue("quotation_number", "");
                                    formik.setFieldValue("quotation_date", "");
                                    formik.setFieldValue("valid_until_date", "");
                                    formik.setFieldValue("enquiry", "");
                                    formik.setFieldValue("enquiry_number", "");
                                    formik.setFieldValue("reference_number", "");
                                    formik.setFieldValue("initiated_by", "");
                                    formik.setFieldValue("company_name", "");
                                    formik.setFieldValue("company_id", "");
                                    formik.setFieldValue("company_email", "");
                                    formik.setFieldValue("industry", "");
                                    formik.setFieldValue("industry_name", "");
                                    formik.setFieldValue("gstin", "");
                                    formik.setFieldValue("gst_type", "");
                                    formik.setFieldValue("contactPersonArray", []);
                                    formik.setFieldValue("contactPerson", {});
                                    formik.setFieldValue("company", null);
                                    formik.setFieldValue("billing_address", {
                                      address: "",
                                      city: "",
                                      state: "",
                                      country: "",
                                      pin_code: "",
                                      phone_number: "",
                                    });
                                    formik.setFieldValue("shipping_addresses", []);
                                    formik.setFieldValue("currency_applicable", false);
                                    formik.setFieldValue("currency_code", "INR");
                                    formik.setFieldValue("currency_symbol", "₹");
                                    formik.setFieldValue("exchange_rate", "1");
                                    formik.setFieldValue("mode_of_emd", "");
                                    formik.setFieldValue("emd_amount", "");
                                    formik.setFieldValue("inspection_applicable", false);
                                    formik.setFieldValue("inspection", "Not Applicable");
                                    formik.setFieldValue("inspection_date", "");
                                    formik.setFieldValue("installation", "Not Applicable");
                                    formik.setFieldValue("installation_date", "");
                                    formik.setFieldValue("customer_po_number", "");
                                    formik.setFieldValue("customer_po_date", "");
                                    formik.setFieldValue("customer_po_document_url", null);
                                    formik.setFieldValue("notes", "");
                                    formik.setFieldValue("attachments", []);
                                    formik.setFieldValue("terms_and_conditions", []);
                                    formik.setFieldValue("agent_name", "");
                                    formik.setFieldValue("composition_gst", false);
                                    formik.setFieldValue("select_composition_gst", "");
                                    setItems([]);
                                    setShippingAddresses([]);
                                  }
                                }}
                                onBlur={() =>
                                  formik.setFieldTouched("quotation", true)
                                }
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderInput={(params) => (
                                  <BootstrapTextField
                                    {...params}
                                    placeholder="Select Quotation"
                                    size="small"
                                    error={
                                      formik.touched.quotation &&
                                      !!formik.errors.quotation
                                    }
                                    helperText={
                                      formik.touched.quotation &&
                                      formik.errors.quotation
                                    }
                                    sx={autocompleteDropdownStyles.inputSx}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>

                          {/* Enquiry Reference Number */}
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Enquiry Reference Number</Form.Label>

                              <Form.Control
                                type="text"
                                name="enquiry_number"
                                value={formik.values.enquiry_number}
                                disabled
                                isInvalid={
                                  formik.touched.enquiry_number &&
                                  !!formik.errors.enquiry_number
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.enquiry_number}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {/* Initiated By */}
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                Initiated By <span className="text-danger">*</span>
                              </Form.Label>

                              <Form.Control
                                type="text"
                                name="initiated_by"
                                placeholder="Enter Name"
                                value={formik.values.initiated_by}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.initiated_by &&
                                  !!formik.errors.initiated_by
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.initiated_by}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    {/* Customer Details Section */}
                    <Col xs={12} md={6}>
                      <div className="rounded mb-3">
                        <div className="d-flex justify-content-start align-items-center mb-3 gap-2">
                          <h5 className="fw-semibold mb-0 me-1">Customer Details</h5>
                          <Button
                            size="sm"
                            variant="light"
                            className="fw-semibold mb-1 rounded"
                            onClick={() => setShowCompanyModal(true)}
                            disabled={!!(formik.values.quotation || formik.values.quotation_number)}
                            style={{
                              backgroundColor: MecordColors?.LightGrayColor,
                            }}
                          >
                            + Add New Company
                          </Button>
                        </div>
                        <Row
                          className="g-3 pb-3 px-1 rounded"
                          style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                        >
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>
                                Select Customer <span className="text-danger">*</span>
                              </Form.Label>

                              <Autocomplete
                                disabled={!!(formik.values.quotation || formik.values.quotation_number)}
                                options={companies}
                                getOptionLabel={(option) => option?.company_name || ""}
                                isOptionEqualToValue={(option, value) => {
                                  if (!option || !value) return false;

                                  // Get IDs from both option and value, ensuring they're not empty strings
                                  const optionCompanyId = option.company_id;
                                  const optionId = option.id;
                                  const valueCompanyId = value.company_id;
                                  const valueId = value.id;

                                  // Only compare if IDs are truthy (not empty strings, null, or undefined)
                                  // Priority: exact matches first (company_id to company_id, id to id)
                                  if (optionCompanyId && valueCompanyId && optionCompanyId === valueCompanyId) {
                                    return true;
                                  }
                                  if (optionId && valueId && optionId === valueId) {
                                    return true;
                                  }

                                  // Cross-match only if exact matches didn't work
                                  if (optionCompanyId && valueId && optionCompanyId === valueId) {
                                    return true;
                                  }
                                  if (optionId && valueCompanyId && optionId === valueCompanyId) {
                                    return true;
                                  }

                                  return false;
                                }}
                                value={
                                  // Only show company if it has a valid ID (not empty object)
                                  formik.values.company &&
                                    (formik.values.company.company_id || formik.values.company.id) &&
                                    (formik.values.company.company_id !== "" && formik.values.company.id !== "")
                                    ? companies.find((c) => {
                                      const formCompanyId = formik.values.company?.company_id;
                                      const formId = formik.values.company?.id;
                                      const cCompanyId = c.company_id;
                                      const cId = c.id;

                                      // Exact matches first
                                      if (formCompanyId && cCompanyId && formCompanyId === cCompanyId) return true;
                                      if (formId && cId && formId === cId) return true;

                                      // Cross-matches
                                      if (formCompanyId && cId && formCompanyId === cId) return true;
                                      if (formId && cCompanyId && formId === cCompanyId) return true;

                                      return false;
                                    }) || null
                                    : null
                                }
                                onChange={async (_, newValue) => {
                                  if (newValue) {
                                    // Check if we're changing to a different company
                                    const currentCompanyId = formik.values.company?.company_id || formik.values.company?.id;
                                    const newCompanyId = newValue.company_id || newValue.id || "";
                                    const isChangingCompany = currentCompanyId && currentCompanyId !== newCompanyId;

                                    // If changing company and there are items, show confirmation
                                    if (isChangingCompany && items && items.length > 0) {
                                      const confirmed = await showConfirm(
                                        "Change Customer?",
                                        "Changing the customer will clear all products. Do you want to continue?",
                                        "Yes, change customer",
                                        "Cancel"
                                      );

                                      if (!confirmed?.isConfirmed) {
                                        return; // User cancelled, don't change company
                                      }
                                      setItems([]);


                                    }

                                    // Map API response structure to form structure
                                    const companyId = newCompanyId;

                                    // Transform contacts from API format to form format
                                    const transformedContacts = (newValue.contacts || []).map((contact: any) => ({
                                      id: contact.contact_id || contact.id || "",
                                      contact_person_name: contact.name || contact.contact_person_name || "",
                                      contact_person_email: contact.email || contact.contact_person_email || "",
                                      contact_person_phone_no: contact.phone_number || contact.contact_person_phone_no || "",
                                      contact_person_designation: contact.designation || contact.contact_person_designation || "",
                                      contact_person_department: contact.contact_person_department || "",
                                    }));

                                    // Create company object with proper structure
                                    const companyObj = {
                                      id: companyId,
                                      company_id: companyId,
                                      company_name: newValue.company_name || "",
                                      industry: newValue.industry || "", // For form display
                                      industry_name: newValue.industry || "", // For formik values
                                      vendor_code: newValue.vendor_code || "",
                                      branch_name: newValue.branch_name || "",
                                      contact_details: transformedContacts,
                                      gstin: newValue.gstin || "",
                                      gst_type: newValue.gst_type || "",
                                      phone_number: newValue.phone_number || "",
                                      sr_no: newValue.sr_no || 1,
                                    
                                    };

                                    // Set company object
                                    formik.setFieldValue("company", companyObj);

                                    // Set top-level company fields
                                    formik.setFieldValue(
                                      "company_name",
                                      newValue.company_name || ""
                                    );
                                    setTimeout(() => formik.setFieldError("company_name", undefined), 0);
                                    formik.setFieldValue(
                                      "company_email",
                                      newValue.company_email || ""
                                    );
                                    formik.setFieldValue(
                                      "industry",
                                      newValue.industry || ""
                                    );
                                    formik.setFieldValue(
                                      "company_id",
                                      companyId
                                    );

                                    // Set billing address (map API structure to form structure)
                                    const billingAddr = newValue.billing_address || {};
                                    formik.setFieldValue(
                                      "billing_address",
                                      {
                                        address: billingAddr.address || "",
                                        city: billingAddr.city || "",
                                        state: billingAddr.state || "",
                                        country: billingAddr.country || "",
                                        pin_code: billingAddr.pincode || billingAddr.pin_code || "",
                                        phone_number: billingAddr.phone_number || "",
                                      }
                                    );

                                    // Set shipping addresses in state & Formik
                                    const formattedAddresses = (
                                      newValue.shipping_addresses || []
                                    ).map((addr: any) => ({
                                      id: addr.shipping_address_id || addr.id || "",
                                      address: addr.address || "",
                                      state: addr.state || "",
                                      city: addr.city || "",
                                      pin_code: addr.pincode || addr.pin_code || "",
                                      phone_number: addr.phone_number || "",
                                      country: addr.country || "",
                                    }));

                                    setShippingAddresses(formattedAddresses); // for dropdown/state
                                    formik.setFieldValue(
                                      "shipping_addresses",
                                      formattedAddresses
                                    ); // for formik

                                    // Don't auto-select contact persons - let user choose
                                    formik.setFieldValue("contactPersonArray", []);
                                    formik.setFieldValue("contactPerson", {});
                                  } else {
                                    // Customer is being removed - check if there are items
                                    const handleCompanyRemoval = async () => {
                                      const hasItems = items && items.length > 0;

                                      if (hasItems) {
                                        const confirmed = await showConfirm(
                                          "Remove Customer?",
                                          "Removing the customer will clear all products. Do you want to continue?",
                                          "Yes, remove customer",
                                          "Cancel"
                                        );

                                        if (!confirmed?.isConfirmed) {
                                          return; // User cancelled, don't remove company
                                        }

                                        // Clear items
                                        setItems([]);
                                      }

                                      // Clear fields if no company selected
                                      formik.setFieldValue("company", null);
                                      formik.setFieldValue("company_name", "");
                                      formik.setFieldValue("company_email", "");
                                      formik.setFieldValue("industry_name", "");
                                      formik.setFieldValue("company_id", "");
                                      formik.setFieldValue("billing_address", {});
                                      formik.setFieldValue("contactPerson", {});
                                      formik.setFieldValue("contactPersonArray", []);
                                      setShippingAddresses([]);
                                      formik.setFieldValue("shipping_addresses", []);
                                    };

                                    handleCompanyRemoval();
                                  }
                                }}
                                onBlur={() => {
                                  formik.setFieldTouched("company_name", true);
                                  formik.validateField("company_name");
                                }}
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderOption={(props, option, { selected }) => {
                                  const { key, ...otherProps } = props;
                                  return (
                                    <li key={key} {...otherProps} style={{ padding: 0 }}>
                                      <Box
                                        sx={{
                                          px: 1.5,
                                          py: 0.8,
                                          width: "100%",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          "&:hover": {
                                            backgroundColor: MecordColors.Hover,
                                            color: MecordColors.ActiveText,
                                          },
                                          ...(selected && {
                                            backgroundColor: MecordColors.ActiveBackground,
                                            color: MecordColors.ActiveText,
                                          }),
                                        }}
                                      >
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                          <span>{option.company_name}</span>
                                          {option.branch_name && (
                                            <span
                                              style={{
                                                fontSize: "0.75rem",
                                                color: "#6c757d",
                                                marginTop: 2,
                                              }}
                                            >
                                              {option.branch_name}
                                            </span>
                                          )}
                                        </Box>
                                      </Box>
                                    </li>
                                  );
                                }}
                                renderInput={(params: any) => (
                                  <BootstrapTextField
                                    {...params}
                                    placeholder="Select Customer"
                                    variant="outlined"
                                    size="small"
                                    error={
                                      formik.touched.company_name &&
                                      !!formik.errors?.company_name
                                    }
                                    helperText={
                                      formik.touched.company_name &&
                                        formik.errors?.company_name
                                        ? (formik.errors.company_name as string)
                                        : ""
                                    }
                                    sx={autocompleteDropdownStyles.inputSx}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Industry</Form.Label>
                              <Form.Control
                                type="text"

                                placeholder="Autofill"
                                value={formik?.values?.company?.industry || ""}
                                disabled
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>GST Type</Form.Label>
                              <Form.Control
                                type="text"
                                disabled
                                placeholder="Autofill"
                                value={formik?.values?.company?.gst_type || ""}

                              />
                            </Form.Group>
                          </Col>

                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>
                                Contact Person <span className="text-danger">*</span>
                              </Form.Label>
                              {/* Autocomplete for adding contact persons */}
                              <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={formik.values.company?.contact_details || []}
                                getOptionLabel={(option: any) =>
                                  option.contact_person_name ||
                                  option.name ||
                                  ""
                                }
                                isOptionEqualToValue={(option: any, value: any) => {
                                  if (!option || !value) return false;

                                  // Match by ID (direct match)
                                  if (option.id && value.id && option.id === value.id) {
                                    return true;
                                  }

                                  // Match by contact_id (direct match)
                                  if (option.contact_id && value.contact_id && option.contact_id === value.contact_id) {
                                    return true;
                                  }

                                  // Cross-match: option.id with value.contact_id
                                  if (option.id && value.contact_id && option.id === value.contact_id) {
                                    return true;
                                  }

                                  // Cross-match: option.contact_id with value.id
                                  if (option.contact_id && value.id && option.contact_id === value.id) {
                                    return true;
                                  }

                                  // Match by email as fallback
                                  const optionEmail = option.contact_person_email || option.email || "";
                                  const valueEmail = value.contact_person_email || value.email || "";
                                  if (optionEmail && valueEmail && optionEmail === valueEmail) {
                                    return true;
                                  }

                                  return false;
                                }}
                                value={formik.values.contactPersonArray || []}
                                onChange={(_, newValue: any[]) => {
                                  // Remove duplicates using ID, contact_id, or email
                                  const uniqueContacts = newValue.filter(
                                    (v, i, a) =>
                                      a.findIndex(
                                        (item: any) => {
                                          // Match by ID (direct)
                                          if (item.id && v.id && item.id === v.id) return true;
                                          // Match by contact_id (direct)
                                          if (item.contact_id && v.contact_id && item.contact_id === v.contact_id) return true;
                                          // Cross-match: item.id with v.contact_id
                                          if (item.id && v.contact_id && item.id === v.contact_id) return true;
                                          // Cross-match: item.contact_id with v.id
                                          if (item.contact_id && v.id && item.contact_id === v.id) return true;
                                          // Match by email
                                          const itemEmail = item.contact_person_email || item.email || "";
                                          const vEmail = v.contact_person_email || v.email || "";
                                          if (itemEmail && vEmail && itemEmail === vEmail) return true;
                                          return false;
                                        }
                                      ) === i
                                  );

                                  formik.setFieldValue(
                                    "contactPersonArray",
                                    uniqueContacts
                                  );
                                }}
                                onBlur={() =>
                                  formik.setFieldTouched("contactPersonArray", true)
                                }
                                renderTags={() => null}
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderOption={(props: any, option: any, { selected }: any) => {
                                  const { key, ...otherProps } = props;
                                  const label =
                                    option?.contact_person_name || option?.name || "";

                                  return (
                                    <li key={key} {...otherProps} style={{ padding: 0 }}>
                                      <div
                                        style={{
                                          width: "100%",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          padding: "8px 12px",
                                          backgroundColor: selected
                                            ? MecordColors.ActiveBackground
                                            : "transparent",
                                          color: selected
                                            ? MecordColors.ActiveText
                                            : "inherit",
                                        }}
                                      >
                                        <span>{label}</span>
                                        {selected && (
                                          <span
                                            style={{
                                              cursor: "pointer",
                                              marginLeft: "10px",
                                              color: "#d32f2f",
                                              fontWeight: "bold",
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();

                                              const current =
                                                formik.values.contactPersonArray || [];

                                              const isSame = (a: any, b: any) => {
                                                if (!a || !b) return false;

                                                // Match by ID (direct match)
                                                if (a.id && b.id && a.id === b.id) return true;
                                                // Match by contact_id (direct match)
                                                if (
                                                  a.contact_id &&
                                                  b.contact_id &&
                                                  a.contact_id === b.contact_id
                                                )
                                                  return true;
                                                // Cross-match: a.id with b.contact_id
                                                if (a.id && b.contact_id && a.id === b.contact_id)
                                                  return true;
                                                // Cross-match: a.contact_id with b.id
                                                if (
                                                  a.contact_id &&
                                                  b.id &&
                                                  a.contact_id === b.id
                                                )
                                                  return true;

                                                // Match by email as fallback
                                                const aEmail =
                                                  a.contact_person_email || a.email || "";
                                                const bEmail =
                                                  b.contact_person_email || b.email || "";
                                                if (aEmail && bEmail && aEmail === bEmail)
                                                  return true;

                                                return false;
                                              };

                                              const updated = current.filter(
                                                (c: any) => !isSame(option, c)
                                              );

                                              formik.setFieldValue(
                                                "contactPersonArray",
                                                updated,
                                                false
                                              );
                                            }}
                                          >
                                            ✕
                                          </span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                }}
                                renderInput={(params) => (
                                  <BootstrapTextField
                                    {...params}
                                    placeholder="Search Contact Person"
                                    size="small"
                                    error={
                                      formik.touched.contactPersonArray &&
                                      !!formik.errors.contactPersonArray
                                    }
                                    helperText={
                                      formik.touched.contactPersonArray &&
                                        typeof formik.errors.contactPersonArray ===
                                        "string"
                                        ? formik.errors.contactPersonArray
                                        : ""
                                    }
                                    sx={autocompleteDropdownStyles.inputSx}
                                  />
                                )}
                              />

                              {/* Display selected contacts as chips below the input */}
                              <Row>
                                <div className="mt-2 d-flex flex-wrap gap-2">
                                  {(formik.values.contactPersonArray || []).map(
                                    (contact, index) => (
                                      <div
                                        key={(contact as any).id || (contact as any).contact_id || index}
                                        className="d-flex align-items-center px-2 py-1 rounded"
                                        style={{
                                          background: MecordColors?.LightThemeBGColor,
                                          border: `1px solid ${MecordColors?.ThemeBGColor}`,
                                          fontSize: "14px",
                                        }}
                                      >
                                        {contact.contact_person_name || (contact as any).name || ""}
                                        <span
                                          style={{
                                            marginLeft: "8px",
                                            cursor: "pointer",
                                            color: "red",
                                            fontWeight: "bold",
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const currentContacts = formik.values.contactPersonArray || [];
                                            const updated = currentContacts.filter((_, i) => i !== index);
                                            formik.setFieldValue(
                                              "contactPersonArray",
                                              updated,
                                              false
                                            );
                                          }}
                                        >
                                          ✕
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </Row>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                Customer PO Number
                                {/* <span className="text-danger">*</span> */}
                              </Form.Label>

                              <Form.Control
                                name="customer_po_number"
                                type="text"
                                placeholder="Enter PO Number"
                                value={formik.values.customer_po_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.customer_po_number &&
                                  !!formik.errors.customer_po_number
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.customer_po_number}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>
                                PO Date
                                {/* <span className="text-danger">*</span> */}
                              </Form.Label>

                              <Form.Control
                                name="customer_po_date"
                                type="date"
                                value={formik.values.customer_po_date}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.customer_po_date &&
                                  !!formik.errors.customer_po_date
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.customer_po_date}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={12}>
                            <Form.Group>
                              {/* <Form.Label>Upload PO Document</Form.Label> */}

                              {/* <Form.Control
                        multiple
                        type="file"
                        name="customer_po_document_url"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(event) => {
                          const input = event.currentTarget as HTMLInputElement;
                          const files = input.files
                            ? Array.from(input.files)
                            : [];

                          formik.setFieldValue("customer_po_document_url", [
                            ...(formik.values.customer_po_document_url || []),
                            ...files,
                          ]);
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          formik.touched.customer_po_document_url &&
                          !!formik.errors.customer_po_document_url
                        }
                      /> */}

                              {/* Show selected file + remove button */}
                              <FileUpload
                                label="Upload PO Document"
                                multiple
                                maxFiles={1}
                                value={formik.values.customer_po_document_url}
                                onChange={(files) =>
                                  formik.setFieldValue(
                                    "customer_po_document_url",
                                    files
                                  )
                                }
                              />

                              <Form.Control.Feedback type="invalid">
                                {formik.errors.customer_po_document_url}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>

                  <hr />

                  {/* Agent Details & Assign Members Section */}
                  <Row className="mb-3">
                    <Col xs={12} md={6}>
                      <div className="rounded mb-3">
                        <div className="d-flex justify-content-start mb-3">
                          <h5 className="fw-semibold mb-2">Agent Details</h5>
                        </div>
                        <Form.Group>
                          <Form.Label>Agent Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="agent_name"
                            placeholder="Enter Agent Name"
                            value={formik.values.agent_name || ""}
                            onChange={(e) =>
                              formik.setFieldValue("agent_name", e.target.value)
                            }
                            onBlur={() => formik.setFieldTouched("agent_name", true)}
                            isInvalid={
                              formik.touched.agent_name &&
                              !!formik.errors.agent_name
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.agent_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    </Col>

                    <Col xs={12} md={6}>
                      <div className="rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3 gap-1">
                          <h5 className="fw-semibold">Add Members</h5>
                        </div>

                        <Row
                          className="g-3 pb-3 px-1 rounded mb-2"
                          style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                        >
                          <Col xs={12} md={12}>
                            <Form.Group>
                              <Form.Label>
                                Assign Members
                                {/* <span className="text-danger">*</span> */}
                              </Form.Label>
                              <Autocomplete
                                options={(Array.isArray(membersOptions) ? membersOptions : []).filter((option: any) => !selectedMemberIds.includes(option.user_id || option.id))}
                                getOptionLabel={(option: any) =>
                                  `${option.first_name || ""} ${option.last_name || ""}`.trim() || ""
                                }
                                isOptionEqualToValue={(option: any, value: any) =>
                                  (option.user_id || option.id) === (value.user_id || value.id)
                                }
                                onChange={(_: any, newValue: any | null) => {
                                  if (newValue) {
                                    handleMemberAdd(newValue);
                                  }
                                }}
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderOption={(props, option, { selected }) => (
                                  <li {...props} style={{ padding: 0 }}>
                                    <Box
                                      sx={{
                                        px: 1.5,
                                        py: 0.8,
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: MecordColors.Hover,
                                          color: MecordColors.ActiveText,
                                        },
                                        ...(selected && {
                                          backgroundColor: MecordColors.ActiveBackground,
                                          color: MecordColors.ActiveText,
                                        }),
                                      }}
                                    >
                                      <span>{`${option.first_name || ""} ${option.last_name || ""}`.trim()}</span>
                                    </Box>
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <BootstrapTextField
                                    {...params}
                                    placeholder="Search User"
                                    size="small"
                                    sx={autocompleteDropdownStyles.inputSx}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="g-3 rounded">
                          {(formik.values.members || []).length > 0 && (
                            <Col xs={12} md={12}>
                              <div className="d-flex flex-wrap gap-2">
                                {(formik.values.members || []).map((member: any) => (
                                  <div
                                    key={member.id}
                                    className="d-flex align-items-center px-2 py-1 rounded gap-2"
                                    style={{
                                      background: MecordColors?.LightThemeBGColor,
                                      border: `1px solid ${MecordColors?.ThemeBGColor}`,
                                      fontSize: "14px",
                                    }}
                                  >
                                    <span
                                      className="rounded-circle d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        fontSize: "0.75rem",
                                        fontWeight: "bold",
                                        backgroundColor: "#fff",
                                        color: MecordColors.ThemeBGColor,
                                      }}
                                    >
                                      {member.initials || getInitials(
                                        member.first_name || "",
                                        member.last_name || ""
                                      )}
                                    </span>
                                    {member.name ||
                                      (member.first_name && member.last_name
                                        ? `${member.first_name} ${member.last_name}`.trim()
                                        : member.fullname || member.username || member.email || "")}
                                    <span
                                      style={{
                                        marginLeft: "4px",
                                        cursor: "pointer",
                                        color: "red",
                                      }}
                                      onClick={() => handleMemberRemove(member.id || member.user_id)}
                                    >
                                      ✕
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col lg={6} md={12}>
                      <AddTags
                        value={formik.values.tags || []}
                        onChange={(tags) => formik.setFieldValue("tags", tags)}
                      />
                    </Col>
                  </Row>

                  <hr />

                  {/* PBG Details Section */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Row className="g-3">
                        <h5 className="mb-3 fw-semibold mb-3" >
                          <Form.Check
                            type="checkbox"
                            label="PBG  (Performance Bank Guarantee) Details"
                            name="pbg_details.pbg_required"
                            checked={formik.values.pbg_details.pbg_required}
                            onChange={formik.handleChange}
                          />
                        </h5>

                        {formik.values.pbg_details.pbg_required && (
                          <>
                            <Row className="mb-3">
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    PBG Number{" "}
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="pbg_details.pbg_number"
                                    placeholder="Enter PBG Number"
                                    value={formik.values.pbg_details.pbg_number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      !!formik.errors.pbg_details?.pbg_number &&
                                      formik.touched.pbg_details?.pbg_number
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formik.touched.pbg_details?.pbg_number &&
                                      formik.errors.pbg_details?.pbg_number}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    PBG Date
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Form.Control
                                    type="date"
                                    name="pbg_details.pbg_date"
                                    value={formik.values.pbg_details.pbg_date}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      !!formik.errors.pbg_details?.pbg_date &&
                                      formik.touched.pbg_details?.pbg_date
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formik.touched.pbg_details?.pbg_date &&
                                      formik.errors.pbg_details?.pbg_date}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    Bank Name{" "}
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="pbg_details.bank_name"
                                    placeholder="Enter Bank Name"
                                    value={formik.values.pbg_details.bank_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      !!formik.errors.pbg_details?.bank_name &&
                                      formik.touched.pbg_details?.bank_name
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formik.errors.pbg_details?.bank_name}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    Amount{" "}
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="pbg_details.amount"
                                    min="0"
                                    value={formik.values.pbg_details.amount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      !!formik.errors.pbg_details?.amount &&
                                      formik.touched.pbg_details?.amount
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formik.errors.pbg_details?.amount}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </Row>

                            <Row className="mb-3">
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    Validity{" "}
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Form.Control
                                    type="date"
                                    name="pbg_details.validity"
                                    value={formik.values.pbg_details.validity}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      !!formik.errors.pbg_details?.validity &&
                                      formik.touched.pbg_details?.validity
                                    }
                                    placeholder="e.g., 12 months"
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {formik.errors.pbg_details?.validity}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>

                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>
                                    PBG Status{" "}
                                    {formik.values.pbg_details.pbg_required && (
                                      <span className="text-danger">*</span>
                                    )}
                                  </Form.Label>
                                  <Autocomplete
                                    options={pbgStatusOptions}
                                    value={formik.values.pbg_details.pbg_status || null}
                                    onChange={(_, newValue) => {
                                      formik.setFieldValue("pbg_details.pbg_status", newValue || "");
                                    }}
                                    onBlur={() => {
                                      formik.setFieldTouched("pbg_details.pbg_status", true);
                                    }}
                                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                    renderOption={(props, option) =>
                                      autocompleteDropdownStyles.renderOption(
                                        props,
                                        option,
                                        (option: any) => option || ""
                                      )
                                    }
                                    renderInput={(params) => (
                                      <BootstrapTextField
                                        {...params}
                                        placeholder="Select Status"
                                        size="small"
                                        error={
                                          !!formik.errors.pbg_details?.pbg_status &&
                                          formik.touched.pbg_details?.pbg_status
                                        }
                                        helperText={
                                          formik.touched.pbg_details?.pbg_status &&
                                          formik.errors.pbg_details?.pbg_status
                                        }
                                        sx={autocompleteDropdownStyles.inputSx}
                                      />
                                    )}
                                  />
                                </Form.Group>
                              </Col>

                              <Col md={3}>
                                <FileUpload
                                  label="Upload Document"
                                  multiple
                                  maxFiles={1}
                                  value={formik.values.pbg_details.upload_document}
                                  onChange={(files) =>
                                    formik.setFieldValue(
                                      "pbg_details.upload_document",
                                      files
                                    )
                                  }
                                />


                              </Col>
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label>PBG Follow Up Date</Form.Label>
                                  <Form.Control
                                    type="date"
                                    name="pbg_details.next_follow_up_date"
                                    value={(formik.values.pbg_details as any)?.next_follow_up_date || ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          </>
                        )}
                      </Row>
                    </Col>
                  </Row>

                  <hr />

                  {/* Currency & Exchange & EMD Details Section */}
                  <Row className="mb-3">
                    <Col xs={12} md={6}>
                      <div className="rounded mb-3">
                        <h5 className="fw-semibold mb-2">
                          <Form.Check
                            type="checkbox"
                            label="Currency & Exchange Details"
                            name="currency_applicable"
                            checked={formik.values.currency_applicable}
                            onChange={(e: any) => {
                              formik.handleChange(e);
                              if (!e.target.checked) {
                                formik.setFieldValue("currency_code", "INR");
                                formik.setFieldValue("currency_symbol", "₹");
                                formik.setFieldValue("exchange_rate", "1");
                                formik.setFieldValue("composition_gst", false);
                                formik.setFieldValue("select_composition_gst", "");
                              }
                            }}
                          />
                        </h5>
                        {formik.values.currency_applicable && (
                          <CurrencyExchangeSection
                            formik={formik}
                            setItems={setItems}
                            currencyList={currencyList || []}
                            onExchangeChange={(value) => setTableExchange(value)}
                          />
                        )}
                      </div>
                    </Col>

                 <Col lg={6} md={12}>

                  <h5 className="fw-semibold mb-3">
                    <Form.Check
                      type="checkbox"
                      label="EMD (Earnest Money Deposit) Details"
                      name="emd_applicable"
                      checked={formik.values.emd_applicable}
                      onChange={(e: any) => {
                        formik.handleChange(e);
                        if (!e.target.checked) {
                          formik.setFieldValue("mode_of_emd", "");
                          formik.setFieldValue("emd_amount", "");
                        }
                      }}
                    />
                  </h5>

                  {formik.values.emd_applicable && (
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Mode of EMD
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Autocomplete
                            options={["BG", "DD", "NEFT/RTGS"]}
                            getOptionLabel={(option: any) => option}
                            value={formik.values.mode_of_emd || null}
                            onChange={(_: any, newValue: any) => {
                              formik.setFieldValue("mode_of_emd", newValue || "");
                              if (!newValue) {
                                formik.setFieldValue("emd_amount", "");
                              }
                            }}
                            onBlur={() => formik.setFieldTouched("mode_of_emd", true)}
                            ListboxProps={autocompleteDropdownStyles.ListboxProps}
                            renderInput={(params: any) => (
                              <BootstrapTextField
                                {...params}
                                placeholder="Select Mode of EMD"
                                size="small"
                                error={
                                  formik.touched.mode_of_emd &&
                                  Boolean(formik.errors.mode_of_emd)
                                }
                                helperText={
                                  formik.touched.mode_of_emd &&
                                    typeof formik.errors.mode_of_emd === "string"
                                    ? formik.errors.mode_of_emd
                                    : ""
                                }
                                sx={autocompleteDropdownStyles.inputSx}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            EMD Amount
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="emd_amount"
                            min="0"
                            value={formik.values.emd_amount || ""}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={
                              !!formik.errors.emd_amount &&
                              formik.touched.emd_amount
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.emd_amount}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                </Col>
                  </Row>


                  <hr />

                  {/* Inspection & Installation Section */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <div className="rounded mb-3">
                        <h5 className="fw-semibold mb-2">
                          <Form.Check
                            type="checkbox"
                            label="Inspection & Installation Details"
                            name="inspection_applicable"
                            checked={formik.values.inspection_applicable}
                            onChange={(e: any) => {
                              formik.handleChange(e);
                              if (!e.target.checked) {
                                formik.setFieldValue("inspection", "");
                                formik.setFieldValue("inspection_date", "");
                                formik.setFieldValue("installation", "");
                                formik.setFieldValue("installation_date", "");
                              }
                            }}
                          />
                        </h5>

                        {formik.values.inspection_applicable && (
                          <Row className="g-3">
                            <Col lg={3} md={6}>
                              <Form.Group className="mb-4">
                                <Form.Label>
                                  Inspection<span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                  options={["Applicable", "Not Applicable"]}
                                  getOptionLabel={(option: any) => option}
                                  isOptionEqualToValue={(option: any, value: any) =>
                                    option === value
                                  }
                                  value={formik.values.inspection || null}
                                  onChange={(_: any, newValue: any) => {
                                    formik.setFieldValue("inspection", newValue || "");
                                    formik.setFieldValue("inspection_date", "");
                                    formik.setFieldTouched("inspection", false);
                                    formik.setFieldTouched("inspection_date", false);
                                  }}
                                  onBlur={() => {
                                    formik.setFieldTouched("inspection", true);
                                    formik.validateField("inspection");
                                  }}
                                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                  renderInput={(params: any) => (
                                    <BootstrapTextField
                                      {...params}
                                      placeholder="Select"
                                      size="small"
                                      error={
                                        !!formik.errors.inspection &&
                                        formik.touched.inspection
                                      }
                                      helperText={
                                        formik.errors.inspection &&
                                          formik.touched.inspection
                                          ? formik.errors.inspection
                                          : ""
                                      }
                                      sx={autocompleteDropdownStyles.inputSx}
                                    />
                                  )}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={3} md={6}>
                              <Form.Group>
                                <Form.Label>
                                  Inspection Date{" "}
                                  {formik?.values?.inspection === "Applicable" && (
                                    <span className="text-danger">*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  name="inspection_date"
                                  value={formik.values.inspection_date}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(
                                      formik.errors.inspection_date &&
                                      formik.touched.inspection_date
                                    )
                                  }
                                />
                                {formik.errors.inspection_date &&
                                  formik.touched.inspection_date && (
                                    <Form.Control.Feedback type="invalid">
                                      {formik.errors.inspection_date as string}
                                    </Form.Control.Feedback>
                                  )}
                              </Form.Group>
                            </Col>
                            <Col lg={3} md={6}>
                              <Form.Group className="mb-4">
                                <Form.Label>
                                  Installation<span className="text-danger">*</span>
                                </Form.Label>
                                <Autocomplete
                                  options={["Applicable", "Not Applicable"]}
                                  getOptionLabel={(option: any) => option}
                                  isOptionEqualToValue={(option: any, value: any) =>
                                    option === value
                                  }
                                  value={formik.values.installation || null}
                                  onChange={(_: any, newValue: any) => {
                                    formik.setFieldValue("installation", newValue || "");
                                    formik.setFieldValue("installation_date", "");
                                    formik.setFieldTouched("installation", false);
                                    formik.setFieldTouched("installation_date", false);
                                  }}
                                  onBlur={() => {
                                    formik.setFieldTouched("installation", true);
                                    formik.validateField("installation");
                                  }}
                                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                  renderInput={(params: any) => (
                                    <BootstrapTextField
                                      {...params}
                                      placeholder="Select"
                                      size="small"
                                      error={
                                        !!formik.errors.installation &&
                                        formik.touched.installation
                                      }
                                      helperText={
                                        formik.errors.installation &&
                                          formik.touched.installation
                                          ? formik.errors.installation
                                          : ""
                                      }
                                      sx={autocompleteDropdownStyles.inputSx}
                                    />
                                  )}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={3} md={6}>
                              <Form.Group>
                                <Form.Label>
                                  Installation Date
                                  {formik?.values?.installation === "Applicable" && (
                                    <span className="text-danger">*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  name="installation_date"
                                  value={formik.values.installation_date}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(
                                      formik.errors.installation_date &&
                                      formik.touched.installation_date
                                    )
                                  }
                                />
                                {formik.errors.installation_date &&
                                  formik.touched.installation_date && (
                                    <Form.Control.Feedback type="invalid">
                                      {formik.errors.installation_date as string}
                                    </Form.Control.Feedback>
                                  )}
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <hr />

                  {/* Billing Details Section */}
                  <Row>
                    {/* BILLING FROM */}
                    <h5 className="fw-semibold mb-3">Billing & Service Details</h5>
                    <div
                      className="p-3 rounded mb-3"
                      style={{ backgroundColor: MecordColors.ActiveBackground }}
                    >
                      <Row>
                        <Col lg={6} md={12}>
                          <BillingFromAddress
                            value={formik.values.bill_from_address}
                            onChange={(newValue) => {
                              // console.log("MasterForm onChange - newValue:", newValue);
                              // console.log("newValue type:", typeof newValue);
                              // console.log("newValue.org_address:", newValue?.org_address);
                              // console.log("newValue.address:", newValue?.address);
                              // console.log("Current bill_from_address value:", formik.values.bill_from_address);
                              
                              // Set the value and let Formik validate automatically
                              formik.setFieldValue("bill_from_address", newValue, true);
                              formik.setFieldTouched("bill_from_address", true, false);
                              
                              // Also explicitly validate after a short delay to ensure state is updated
                              setTimeout(() => {
                                formik.validateField("bill_from_address").then((error) => {
                                  // console.log("Validation result for bill_from_address:", error);
                                  // console.log("Current formik values after validation:", formik.values.bill_from_address);
                                  // console.log("formik.errors.bill_from_address:", formik.errors.bill_from_address);
                                });
                              }, 10);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("bill_from_address", true, false);
                              setTimeout(() => {
                                formik.validateField("bill_from_address");
                              }, 10);
                            }}
                            error={
                              formik.touched.bill_from_address
                                ? formik.errors.bill_from_address || undefined
                                : undefined
                            }
                          />
                        </Col>

                        {/* BILLING TO – OPTIONAL */}
                        <Col lg={6} md={12}>
                          <Form.Group>
                            <Form.Label >Bill to</Form.Label>

                            <Tooltip
                              title={formatFullAddress(formik.values.billing_address) || "No address available"}
                              arrow
                              placement="top"
                            >
                              <Form.Control
                                type="text"
                                name="billing_address.address"
                                placeholder="Autofill"
                                value={formatFullAddress(formik.values.billing_address) || ""}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.billing_address &&
                                  !!formik.errors.billing_address
                                }
                                disabled
                                style={{
                                  cursor:  "pointer",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}
                              />
                            </Tooltip>

                            <Form.Control.Feedback type="invalid">
                              {formik.errors.bill_to_address}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Row>

                  <hr />

                  {/* Product List Section - id for scroll when "at least one product" required */}
                  <Row className="mb-3" id="items">
                    <Col md={12}>
                      <div className="d-flex justify-content-start align-items-center mb-2">
                        <h5 className="fw-semibold mb-0">Product </h5>
                        <Button
                          size="sm"
                          variant="light"
                          className="fw-semibold ms-3"
                          onClick={() => setShowProductModal(true)}
                          style={{
                            backgroundColor: MecordColors?.LightGrayColor,
                            color: "#000",
                          }}
                        >
                          + Add New Product
                        </Button>
                      </div>
                      <ProductAndServiceSection
                        items={items as any[]}
                        setItems={setItems as React.Dispatch<React.SetStateAction<any[]>>}
                        // categories={categories}
                        // subcategories={subcategories}
                        // makes={makes}
                        // models={models}
                        // fetchSubcategories={fetchSubcategories}
                        // fetchMakes={fetchMakes}
                        // fetchModels={fetchModels}
                        // products={products || {}}
                        onAdd={handleProductAdd}
                        billFromAddress={formik.values.bill_from_address}
                        shippingAddresses={shippingAddresses}
                        composition_gst={formik.values.composition_gst}
                        select_composition_gst={formik.values.select_composition_gst}
                        currency_code={formik.values.currency_code}
                        currency_symbol={formik.values.currency_symbol}
                        exchange_rate={formik.values.exchange_rate}
                        setShowProductModal={setShowProductModal}
                        company_gst_type={formik.values.company?.gst_type}
                        billToAddress={formik.values.billing_address}
                        setShowAddressModal={setShowAddressModal}
                        ref={productFormRef}
                      />
                    </Col>
                  </Row>



                  {/* Terms & Conditions Section */}
                  <Row className="mb-3">
                    <Col xs={12} md={6}>

                      <FormikProvider value={formik}>
                        <TermsConditionsSection />
                      </FormikProvider>
                      <Form.Group className="mt-3">
                        <Form.Label>Payment Terms Amount</Form.Label>
                        <Form.Control
                          type="number"
                          name="payment_terms_amount"
                          placeholder="Enter Payment Terms Amount"
                          value={formik.values.payment_terms_amount ?? ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.payment_terms_amount && !!formik.errors.payment_terms_amount}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.payment_terms_amount}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <div className="">
                        <h5 className="fw-semibold mb-3">Total Summary</h5>

                        <div className="rounded p-3" style={{ backgroundColor: MecordColors.LightThemeBGColor }}>
                          <Row className="mb-2">
                            <Col xs={8}>
                              <span>Subtotal (Before Discount):</span>
                            </Col>
                            <Col xs={4} className="text-end">
                              <strong>
                                {formik.values.currency_symbol || (formik.values.currency_code === "INR" || formik.values.currency_code === "IND" ? "₹" : "@")}
                                {formatIndianAmount(totals.subTotal || 0)}
                              </strong>
                            </Col>
                          </Row>
                          <Row className="mb-2">
                            <Col xs={8}>
                              <span>Total Item Discounts:</span>
                            </Col>
                            <Col xs={4} className="text-end">
                              <strong>
                                {formik.values.currency_symbol || (formik.values.currency_code === "INR" || formik.values.currency_code === "IND" ? "₹" : "@")}
                                {formatIndianAmount(totals.discount || 0)}
                              </strong>
                            </Col>
                          </Row>
                          <Row className="mb-2">
                            <Col xs={8}>
                              <span>Net Total:</span>
                            </Col>
                            <Col xs={4} className="text-end">
                              <strong>
                                {formik.values.currency_symbol || (formik.values.currency_code === "INR" || formik.values.currency_code === "IND" ? "₹" : "@")}
                                {formatIndianAmount(totals.netTotal || 0)}
                              </strong>
                            </Col>
                          </Row>
                          <Row className="mb-2">
                            <Col xs={8}>
                              <span>Total GST Value:</span>
                            </Col>
                            <Col xs={4} className="text-end">
                              <strong>
                                {formik.values.currency_symbol || (formik.values.currency_code === "INR" || formik.values.currency_code === "IND" ? "₹" : "@")}
                                {formatIndianAmount(totals.gst || 0)}
                              </strong>
                            </Col>
                          </Row>
                          <hr />
                          <Row>
                            <Col xs={8}>
                              <span className="fw-bold">Grand Total Amount:</span>
                            </Col>
                            <Col xs={4} className="text-end">
                              <strong className="fw-bold" style={{ fontSize: "1.2rem" }}>
                                {formik.values.currency_symbol || (formik.values.currency_code === "INR" || formik.values.currency_code === "IND" ? "₹" : "@")}
                                {formatIndianAmount(totals.grandTotal || 0)}
                              </strong>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <hr />

                  {/* Retention Details Section */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <RetentionDetails
                        value={formik.values.retention_details}
                        onChange={(newValue) =>
                          formik.setFieldValue("retention_details", newValue)
                        }
                        error={
                          formik.touched.retention_details
                            ? formik.errors.retention_details || undefined
                            : undefined
                        }
                        retentionTypeOptions={retentionTypeOptions}
                        baseAmount={totals.netTotal}
                        disabled={saving || savingDraft}
                      />
                    </Col>
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col xs={12} md={6}>
                      <NotesAndAttachments
                        notes={formik.values.notes || ""}
                        onNotesChange={(val) => formik.setFieldValue("notes", val)}
                        notesError={
                          formik.touched.notes ? (formik.errors.notes as any) : undefined
                        }
                        attachments={formik.values.attachments || []}
                        onAttachmentsChange={(files) =>
                          formik.setFieldValue("attachments", files)
                        }
                        disabled={saving || savingDraft}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <ScheduleFollowUp
                        value={{
                          next_follow_up_date: formik.values.next_follow_up_date || "",
                          follow_up_remarks: formik.values.follow_up_remarks || "",
                        }}
                        onChange={(val) => {
                          formik.setFieldValue("next_follow_up_date", val.next_follow_up_date);
                          formik.setFieldValue("follow_up_remarks", val.follow_up_remarks);
                          formik.setFieldTouched("next_follow_up_date", true, false);
                          if (val.next_follow_up_date?.trim()) {
                            setTimeout(() => formik.setFieldError("next_follow_up_date", undefined), 0);
                          }
                        }}
                        errors={{
                          next_follow_up_date:
                            formik.touched.next_follow_up_date
                              ? (formik.errors.next_follow_up_date as any)
                              : undefined,
                          follow_up_remarks:
                            formik.touched.follow_up_remarks
                              ? (formik.errors.follow_up_remarks as any)
                              : undefined,
                        }}
                        disabled={saving || savingDraft}
                      />
                    </Col>
                  </Row>
                  <hr />

                  {/* Bottom Action Buttons */}
                  <Row className="mt-4">
                    <Col xs={12}>
                      <FormActionButtons
                        onCancel={() => navigate(`/sales_order/product/sales_order_log`)}
                        onSaveDraft={async () => {
                          // For draft, validate items but allow other fields to be incomplete
                          if (!items || items.length === 0) {
                            toast.error("At least one product is required");
                            setTimeout(() => document.getElementById("items")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
                            return;
                          }

                          try {
                            await handleSubmitAction(formik.values, "draft");
                            // toast.success("Sales order saved as draft successfully");
                            navigate(`/sales_order/product/draft`);
                          } catch (err) {
                            // Error already handled in handleSubmitAction with toast
                          }
                        }}
                        onSave={async () => {
                          // Mark all fields as touched to show validation errors
                          const fields = Object.keys(formik.values);
                          const touchedFields: any = {};
                          fields.forEach((field) => {
                            touchedFields[field] = true;
                          });
                          formik.setTouched(touchedFields);

                          // Validate all fields first
                          const errors = await formik.validateForm();

                          // If there are validation errors, don't save
                          if (Object.keys(errors).length > 0) {
                            formik.setErrors(errors);
                            setValidationAttemptCount((c) => c + 1);
                            // Show toast with first error message to help user
                            const firstError = Object.values(errors).find((err: any) =>
                              typeof err === 'string' && err.trim() !== ''
                            ) || "Please fix the validation errors";
                            if (typeof firstError === 'string') {
                              toast.error(firstError);
                            }
                            return;
                          }

                          // Validate items before submission
                          if (!items || items.length === 0) {
                            toast.error("At least one product is required");
                            setTimeout(() => document.getElementById("items")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
                            return;
                          }

                          try {
                            await handleSubmitAction(formik.values, "save");
                            // toast.success("Sales order saved successfully");
                            navigate(`/sales_order/product/sales_order_log`);
                          } catch (err) {
                            // Error already handled in handleSubmitAction with toast
                          }
                        }}
                        isSaving={saving}
                        isSavingDraft={savingDraft}
                        wrapperClassName="d-flex flex-wrap justify-content-center gap-md-3"
                      />
                    </Col>
                  </Row>
                </Form>
              </>
            );
          }}
        </Formik>
 
   

        <AddCustomerModal
          show={showCompanyModal}
          onHide={() => setShowCompanyModal(false)}
          onSave={async () => {
            await dispatch(fetchCompanyByDropDown());
          }}
        />
        {/* </Modal.Body> */}
        {/* </Modal> */}
        <AddProductModal
          show={showProductModal}
          onHide={() => setShowProductModal(false)}
          onSave={async () => {
            await dispatch(fetchCategories("products"));
          }}
        />
        <Modal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          backdrop="static"
          keyboard={false}
          size="xl"
          centered
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: MecordColors?.ThemeBGColor,
              paddingBottom: "20px",
            }}
          >
            <Modal.Title style={{ color: "#fff" }}>
              Add New Address
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
            {formik.values.company && (
              <AddressFormModal
                company_id={formik.values.company?.company_id || formik.values.company?.id}
                formik={formik}
                onSuccess={async (response: any) => {
                  // Only refetch company dropdown, don't refresh the page
                  const companiesResult = await dispatch(fetchCompanyByDropDown()).unwrap();
                  const updatedCompanies = companiesResult?.data || companiesResult || [];

                  // Find the updated company with new shipping addresses
                  const currentCompanyId = formik.values.company?.company_id || formik.values.company?.id;
                  const updatedCompany = updatedCompanies.find(
                    (c: any) => c.id === currentCompanyId || c.company_id === currentCompanyId
                  );

                  // Update company object in formik with new shipping addresses
                  if (updatedCompany && updatedCompany.shipping_addresses) {
                    // Store previous address count and IDs BEFORE updating (to identify the new address)
                    const previousAddressCount = formik.values.company?.shipping_addresses?.length || 0;
                    const previousAddressIds = new Set(
                      (formik.values.company?.shipping_addresses || []).map((addr: any) =>
                        addr.id || addr.shipping_address_id
                      ).filter(Boolean)
                    );

                    const formattedAddresses = updatedCompany.shipping_addresses.map(
                      (addr: any) => ({
                        id: addr.id || addr.shipping_address_id,
                        address: addr.address || `${addr.city}, ${addr.state}, ${addr.pin_code}`,
                        state: addr.state,
                        city: addr.city,
                        pin_code: addr.pincode || addr.pin_code,
                        phone_number: addr.phone_number,
                      })
                    );

                    // Update shipping addresses state
                    setShippingAddresses(formattedAddresses);

                    // Update company object in formik
                    formik.setFieldValue("company", {
                      ...formik.values.company,
                      shipping_addresses: updatedCompany.shipping_addresses,
                    }, false);
                    // console.log("response => in masterform add shipping address", response)
                    // Directly set the newly added address in DynamicProductAndServiceForm (like quotation form)
                    if (response && response.data && productFormRef.current) {
                      // Get the newly created address from response (response.data.data.createdAddress)
                      const newAddress = response.data?.data?.createdAddress || response.data?.createdAddress || response.data;
                      // console.log("newAddress => in masterform add shipping address", newAddress)

                      // The newAddress from response is the most reliable source - use it directly
                      // Extract the ID to verify it exists in updatedCompany
                      const newAddressId = newAddress?.shipping_address_id || newAddress?.id;
                      // console.log("newAddressId => in masterform add shipping address", newAddressId)

                      let fullNewAddress = newAddress; // Default to using the response address directly

                      // Try to find the matching address in updatedCompany to get the full object structure
                      // This ensures we have all fields that might be in the company's address list
                      if (newAddressId && updatedCompany.shipping_addresses && updatedCompany.shipping_addresses.length > 0) {
                        // console.log("updatedCompany.shipping_addresses => in masterform add shipping address", updatedCompany.shipping_addresses)

                        // Find the address with matching ID - use strict comparison
                        const foundAddress = updatedCompany.shipping_addresses.find(
                          (addr: any) => {
                            const addrShippingId = addr.shipping_address_id || addr.id;
                            const addrId = addr.id;

                            // Strict match: both IDs must be defined and match exactly
                            return (newAddressId && addrShippingId && addrShippingId === newAddressId) ||
                              (newAddressId && addrId && addrId === newAddressId);
                          }
                        );

                        // Only use foundAddress if it actually matches (has the same ID)
                        if (foundAddress) {
                          const foundId = foundAddress.shipping_address_id || foundAddress.id;
                          if (foundId === newAddressId) {
                            fullNewAddress = foundAddress;
                            // console.log("fullNewAddress found by ID match => in masterform add shipping address", fullNewAddress)
                          } else {
                            // console.log("Found address but ID mismatch, using newAddress from response")
                          }
                        } else {
                          // console.log("Address not found in updatedCompany, using newAddress from response")
                        }
                      }

                      // Final fallback: if we still don't have a match and count increased, use the last address
                      if (!fullNewAddress && updatedCompany.shipping_addresses && updatedCompany.shipping_addresses.length > previousAddressCount) {
                        const lastAddress = updatedCompany.shipping_addresses[updatedCompany.shipping_addresses.length - 1];
                        const lastAddressId = lastAddress?.shipping_address_id || lastAddress?.id;
                        // Only use last address if its ID matches the new address ID
                        if (lastAddressId === newAddressId) {
                          fullNewAddress = lastAddress;
                          // console.log("fullNewAddress using last address (ID matched) => in masterform add shipping address", fullNewAddress)
                        }
                      }

                      // console.log("final fullNewAddress => in masterform add shipping address", fullNewAddress)

                      // Wait a bit for shippingAddressOptions to update, then call the method
                      setTimeout(() => {
                        if (productFormRef.current?.setShippingAddress && fullNewAddress) {
                          productFormRef.current.setShippingAddress(fullNewAddress);
                        }
                      }, 100);
                    }
                  }

                  setShowAddressModal(false);
                }}
                onCancel={() => setShowAddressModal(false)}
              />
            )}
          </Modal.Body>
        </Modal>
      </PageContainer>
    );
  };

export default AddProductAndServicesSalesOrderForm;

